import React, { useState, useEffect, useCallback, useRef } from 'react'
import styled from 'styled-components'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react'
import axios from 'axios'

const Container = styled.div`
  background-color: #f5f5f5;
  min-height: calc(100vh - 80px);
  padding: 0;

  @media (min-width: 681px) {
    display: flex;
    flex-direction: row;
  }
`

// 데스크톱용 좌우 섹션
const LeftSection = styled.div`
  display: none;

  @media (min-width: 681px) {
    display: flex;
    flex-direction: column;
    flex: 1.8;
    min-width: 0;
    background-color: #f5f7fb;
    border-right: 1px solid #e5e7eb;
    height: calc(100vh - 60px);
    overflow-y: auto;
    padding: 20px;
    gap: 16px;
  }
`

const RightSection = styled.div`
  display: none;

  @media (min-width: 681px) {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    background-color: white;
    height: calc(100vh - 60px);
    overflow-y: auto;
    padding: 20px;
  }
`

// 모바일용 기존 컴포넌트들
const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (min-width: 681px) {
    display: none;
  }
`

// 데스크톱용 스타일 컴포넌트들
const DesktopHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 0 20px;
  margin-bottom: 20px;
`

const DesktopSectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`

const CompleteButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #218838;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`

const DesktopARStageContainer = styled.div`
  padding: 20px 20px 0 20px;
  display: flex;
  flex-direction: column;
  flex: 0.9;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  gap: 0;
`

const DesktopStageSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0;
`

const DesktopButtonSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  margin: 0;
`

const DesktopARStage = styled.div`
  position: relative;
  width: 280px;
  height: 200px;
  background-image: url('/ArStage.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
`

const ARObject = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -120%);
  transition: all 0.5s ease;
  
  img {
    width: 150px;
    height: auto;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
  }
`

// 퀴즈 제목 표시 컴포넌트
const QuizTitleHeader = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px 18px;
  margin: 0;

  @media (max-width: 680px) {
    margin: 15px 20px 10px 20px;
  }
`

const QuizTitleText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  margin-bottom: 6px;
`

const QuizTitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.3px;

  @media (max-width: 680px) {
    white-space: normal;
    word-break: break-word;
  }
`

const QuizId = styled.div`
  display: inline-block;
  font-size: 11px;
  color: #64748b;
  margin-top: 6px;
  padding: 3px 9px;
  background: #f1f5f9;
  border-radius: 999px;
  font-weight: 600;
`

const ObjectBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 0 0;
  padding: 12px 16px;
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%);
  border-radius: 12px;
  font-size: 15px;
  font-weight: 800;
  color: #ffffff;
  box-shadow: 0 6px 14px rgba(124, 58, 237, 0.30);
  letter-spacing: -0.2px;

  &::before {
    content: '📦';
    font-size: 18px;
  }

  .label {
    font-size: 11.5px;
    font-weight: 600;
    opacity: 0.85;
    letter-spacing: 0.3px;
    margin-right: 2px;
  }

  .name {
    font-size: 17px;
    font-weight: 800;
  }

  .index {
    margin-left: auto;
    font-size: 12px;
    font-weight: 600;
    opacity: 0.85;
    background: rgba(255,255,255,0.18);
    padding: 3px 9px;
    border-radius: 999px;
  }
`

const NavButton = styled.button`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #2196F3;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #2196F3;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    color: #ccc;
    cursor: not-allowed;
    box-shadow: none;
    
    &:hover {
      transform: none;
    }
  }
`

// 모바일용 컴포넌트들
const CompactARSection = styled.div`
  padding: 15px 20px;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid #e0e0e0;
`

const CompactARStage = styled.div`
  position: relative;
  width: 180px;
  height: 135px;
  background-image: url('/Arstage.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    width: 150px;
    height: 110px;
  }
`

const CompactARObject = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -120%);
  
  img {
    width: 80px;
    height: auto;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
    
    @media (max-width: 768px) {
      width: 60px;
    }
  }
`

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  max-width: 350px;
  gap: 20px;
  font-size: 14px;
  
  @media (max-width: 768px) {
    max-width: 300px;
    gap: 15px;
  }
  
  @media (max-width: 480px) {
    max-width: 260px;
    gap: 10px;
  }
`

const MobileNavButton = styled.button`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #2196F3;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #2196F3;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    color: #ccc;
    cursor: not-allowed;
    box-shadow: none;
    
    &:hover {
      transform: none;
    }
  }
`

const TimeBar = styled.div`
  background: linear-gradient(90deg, #9C27B0 0%, #E91E63 100%);
  color: white;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
`

const TimeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const TimeInput = styled.input`
  background: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  width: 50px;
  text-align: center;
  font-weight: 600;
  color: #333;
`

const SaveText = styled.span`
  cursor: pointer;
  font-weight: 600;
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
  
  &:active {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(0.98);
  }
`

// 데스크톱용 문제 작성 섹션
const DesktopQuestionSection = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px;
  flex: 0 0 auto;
`

const DesktopTimeSection = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px 18px;
  margin-bottom: 0;
  display: inline-flex;
  flex-direction: column;
  gap: 6px;

  &:not(:last-child) { margin-right: 12px; }
`

const TimeLabel = styled.label`
  font-size: 12.5px;
  font-weight: 700;
  color: #475569;
  display: block;
`

const TimeInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '⏱';
    font-size: 16px;
    color: #7c3aed;
  }
`

const DesktopTimeInput = styled.input`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 10px;
  width: 90px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  background: #ffffff;
  font-family: inherit;

  &::placeholder {
    color: #cbd5e1;
    font-weight: 600;
  }

  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124,58,237,0.10);
  }
`

// 모바일용 문제 섹션
const QuestionSection = styled.div`
  padding: 20px;
  background-color: #f5f5f5;
  flex: 1;
  min-height: auto;
`

const QuestionPrompt = styled.div`
  text-align: center;
  margin-bottom: 20px;
  font-size: 16px;
  color: #666;
  font-weight: 500;
`

const QuestionInput = styled.textarea`
  width: 100%;
  max-width: 100%;
  margin: 0;
  min-height: 110px;
  padding: 14px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.55;
  resize: vertical;
  background: #ffffff;
  color: #0f172a;
  box-sizing: border-box;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124,58,237,0.10);
  }
  &::placeholder {
    color: #94a3b8;
  }
`

const QuestionInputContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
`

const IconsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  margin: 12px 0 0 0;
  width: 100%;
`

const IconButton = styled.button`
  width: 100%;
  min-height: 50px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #475569;
  font-size: 13px;
  font-weight: 700;

  &:hover {
    border-color: #7c3aed;
    color: #7c3aed;
    background: #f5f3ff;
    transform: translateY(-1px);
  }

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
  }
`

const AttachmentSection = styled.div`
  margin: 16px 0 24px 0;
  border: 2px solid #e8f4fd;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(33, 150, 243, 0.08);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #4facfe;
    box-shadow: 0 4px 20px rgba(33, 150, 243, 0.12);
  }
`

const AttachmentHeader = styled.div`
  padding: 12px 20px;
  background: linear-gradient(135deg, #f8fbff 0%, #e8f4fd 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: ${props => props.$isOpen ? '1px solid rgba(33, 150, 243, 0.1)' : 'none'};
  
  &:hover {
    background: linear-gradient(135deg, #e8f4fd 0%, #dbeafe 100%);
  }
`

const AttachmentTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  display: flex;
  align-items: center;
  gap: 8px;
`

const AttachmentToggle = styled.span`
  font-size: 12px;
  color: #64748b;
  transition: all 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  
  &::after {
    content: "▼";
  }
`

const AttachmentContent = styled.div`
  padding: ${props => props.$isOpen ? '20px' : '0 20px'};
  max-height: ${props => props.$isOpen ? '400px' : '0'};
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: #ffffff;
`

const AttachmentPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    object-fit: cover;
  }
  
  audio {
    width: 100%;
    height: 40px;
    border-radius: 8px;
  }
`

const AttachmentItem = styled.div`
  position: relative;
  padding: 12px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
`

const DeleteAttachmentButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: #ef4444;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.2s ease;
  z-index: 2;
  
  &:hover {
    background: #dc2626;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`

const AttachmentName = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 8px;
  font-weight: 500;
  text-align: center;
  padding: 6px 12px;
  background: rgba(100, 116, 139, 0.1);
  border-radius: 6px;
  display: inline-block;
`

const ChoicesContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ChoiceContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
`

const ChoiceWrapper = styled.div`
  position: relative;
  flex: 1;
  height: 46px;
  width: 100%;
  background: ${props => props.$isCorrect ? '#ecfdf5' : '#ffffff'};
  border: 1.5px solid ${props => props.$isCorrect ? '#10b981' : '#e5e7eb'};
  border-radius: 10px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${props => props.$isCorrect ? '#059669' : '#7c3aed'};
  }

  &::after {
    content: ${props => props.$isCorrect ? "'정답'" : "''"};
    position: absolute;
    right: 56px;
    top: 50%;
    transform: translateY(-50%);
    background: #10b981;
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 999px;
    pointer-events: none;
    display: ${props => props.$isCorrect ? 'block' : 'none'};
  }
`

const ChoiceInput = styled.input`
  width: calc(100% - 50px);
  height: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  box-sizing: border-box;
  border-radius: 10px;

  &:focus { outline: none; }
  &::placeholder { color: #94a3b8; font-style: normal; }
  &:disabled { color: #94a3b8; }
`

const ChoiceTypeButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: 2px solid ${props => props.$isCorrect ? '#10b981' : '#cbd5e1'};
  background: #ffffff;
  border-radius: 50%;
  cursor: pointer;
  z-index: 2;
  padding: 0;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${props => props.$isCorrect ? '#059669' : '#7c3aed'};
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: ${props => props.$isCorrect ? '#10b981' : 'transparent'};
    transition: background 0.15s;
  }
`


// 수식 입력 모달 스타일
const MathModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`

const MathModalContainer = styled.div`
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
  overflow: hidden;
  
  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: 85vh;
    border-radius: 12px;
  }
`

const MathModalHeader = styled.div`
  background: linear-gradient(45deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  padding: 20px;
  text-align: center;
  
  h2 {
    margin: 0 0 5px 0;
    font-size: 20px;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
    font-size: 14px;
  }
`

const EquationDisplay = styled.div`
  padding: 30px 20px;
  text-align: center;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
`

const Equation = styled.div`
  font-size: 24px;
  font-weight: 500;
  color: #2c3e50;
  line-height: 1.4;
  margin-bottom: 15px;
`

const Coefficient = styled.span`
  display: inline-block;
  min-width: 40px;
  height: 40px;
  line-height: 40px;
  margin: 0 2px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  text-align: center;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  
  &.active {
    border-color: #4facfe;
    background: #4facfe;
    color: white;
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(79, 172, 254, 0.3);
  }
  
  &.filled {
    border-color: #28a745;
    background: #e8f5e8;
    color: #28a745;
  }
  
  &.empty {
    color: #adb5bd;
    border-style: dashed;
  }
`

const ProgressIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin: 15px 0;
`

const ProgressDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e9ecef;
  transition: all 0.3s ease;
  
  &.active {
    background: #4facfe;
    transform: scale(1.2);
  }
  
  &.filled {
    background: #28a745;
  }
`

const InputSection = styled.div`
  padding: 20px;
`

const CoefficientTabs = styled.div`
  display: flex;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 20px;
  gap: 4px;
`

const Tab = styled.button`
  flex: 1;
  height: 44px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6c757d;
  
  &.active {
    background: #4facfe;
    color: white;
    transform: translateY(-1px);
  }
  
  &.filled {
    background: #28a745;
    color: white;
  }
`

const CurrentInput = styled.div`
  text-align: center;
  margin-bottom: 15px;
`

const InputLabel = styled.div`
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 8px;
`

const MathInputField = styled.input`
  width: 100%;
  height: 50px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 20px;
  text-align: center;
  background: white;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #4facfe;
    box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
  }
`

const QuickNumbers = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const QuickBtn = styled.button`
  height: 44px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #4facfe;
    color: white;
    border-color: #4facfe;
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`

const MathBtn = styled.button`
  flex: 1;
  height: 50px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
`

const BtnClear = styled(MathBtn)`
  background: #e9ecef;
  color: #6c757d;
  
  &:hover {
    background: #dc3545;
    color: white;
  }
`

const BtnConfirm = styled(MathBtn)`
  background: linear-gradient(45deg, #28a745, #20c997);
  color: white;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
  }
`

const BtnCancel = styled(MathBtn)`
  background: #6c757d;
  color: white;
  
  &:hover {
    background: #5a6268;
  }
`

// 문제 네비게이션 스타일 컴포넌트들
const QuestionNavigationContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  padding: 16px 18px;
  margin-bottom: 0;
  border-radius: 14px;
`

const ProgressCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  padding: 14px 18px;
  border-radius: 14px;

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 600;
  }
  .head .label { color: #475569; }
  .head .pct {
    color: #7c3aed;
    font-weight: 700;
    font-size: 13px;
  }
  .track {
    width: 100%;
    height: 8px;
    background: #f1f5f9;
    border-radius: 999px;
    overflow: hidden;
  }
  .fill {
    height: 100%;
    background: linear-gradient(90deg, #7c3aed, #4f46e5, #2563eb);
    border-radius: 999px;
    transition: width 0.3s;
  }
`

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 10px;
  letter-spacing: -0.2px;
`

const QuestionTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const QuestionTab = styled.button`
  min-width: 44px;
  height: 40px;
  padding: 0 14px;
  border: 1.5px solid ${props => props.$isActive ? '#7c3aed' : '#e5e7eb'};
  border-radius: 10px;
  background: ${props => props.$isActive ? 'linear-gradient(135deg, rgba(124,58,237,0.10), rgba(37,99,235,0.10))' : '#ffffff'};
  color: ${props => props.$isActive ? '#7c3aed' : '#475569'};
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: ${props => props.$isActive ? '0 4px 10px rgba(124,58,237,0.15)' : 'none'};

  &:hover {
    border-color: #7c3aed;
    color: #7c3aed;
    transform: translateY(-1px);
  }

  ${props => props.$hasContent && `
    &::after {
      content: '✓';
      position: absolute;
      top: -5px;
      right: -5px;
      width: 16px;
      height: 16px;
      background: #10b981;
      border-radius: 50%;
      border: 2px solid #ffffff;
      color: #ffffff;
      font-size: 9px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `}
`

const AddQuestionButton = styled.button`
  background: #28a745;
  border: none;
  border-radius: 8px;
  color: white;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  
  &:hover {
    background: #218838;
    transform: scale(1.05);
  }
`

const AddQuestionTabButton = styled.button`
  min-width: 44px;
  height: 40px;
  padding: 0 12px;
  border: 1.5px dashed #c4b5fd;
  border-radius: 10px;
  background: rgba(124,58,237,0.04);
  color: #7c3aed;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;

  &:hover {
    background: rgba(124,58,237,0.10);
    border-color: #7c3aed;
    transform: translateY(-1px);
  }
`

const QuestionInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
`

const QuestionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const QuestionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`

const ObjectSelectWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.12);
  padding: 6px 10px;
  border-radius: 12px;
`

const ObjectSelectLabel = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
`

const ObjectSelect = styled.select`
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #4a4a4a;
  cursor: pointer;
  min-width: 150px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.35);
  }
`

const DeleteQuestionButton = styled.button`
  background: #dc3545;
  border: none;
  border-radius: 8px;
  color: white;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #c82333;
    transform: scale(1.05);
  }
  
  &:disabled {
    background: rgba(220, 53, 69, 0.5);
    cursor: not-allowed;
    transform: none;
  }
`

/* === 새 디자인: 4단계 stepper 헤더 === */
const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 28px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 50;
  flex-wrap: wrap;

  @media (max-width: 1100px) {
    padding: 12px 16px;
  }
`

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  border: 1px solid #e5e7eb;
  color: #475569;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  &:hover { border-color: #7c3aed; color: #7c3aed; }
`

const StepperWrap = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 720px;
  justify-content: center;
  margin: 0 12px;

  @media (max-width: 1280px) {
    order: 3;
    width: 100%;
    margin: 8px 0 0;
    max-width: none;
  }
`

const StepDot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  .num {
    width: 28px; height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    background: ${p => p.$active ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : '#e5e7eb'};
    color: ${p => p.$active ? '#ffffff' : '#94a3b8'};
    box-shadow: ${p => p.$active ? '0 4px 12px rgba(124,58,237,0.30)' : 'none'};
  }
  .label {
    font-size: 13px;
    font-weight: 600;
    color: ${p => p.$active ? '#0f172a' : '#94a3b8'};
  }
`

const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background: #e5e7eb;
  margin: 0 10px;
  min-width: 16px;
`

const TopActions = styled.div`
  display: flex;
  gap: 8px;
`

const SaveDraftBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  color: #0f172a;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #7c3aed; color: #7c3aed; }
`

const NextStepBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(124,58,237,0.30);
  transition: all 0.15s;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 22px rgba(124,58,237,0.40); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const ObjectNavBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #f5f3ff;
  border: 1px solid #c4b5fd;
  border-radius: 999px;
  font-size: 12px;
  color: #5b21b6;
  font-weight: 600;
`

const ObjectNavBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: ${p => p.disabled ? '#e2e8f0' : '#7c3aed'};
  color: ${p => p.disabled ? '#94a3b8' : '#ffffff'};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) { background: #6d28d9; }
`

/* === AR 요소 설정 우측 sticky 패널 === */
const ARSettingsPanel = styled.aside`
  position: fixed;
  top: 60px;
  right: 0;
  bottom: 0;
  width: 320px;
  background: #ffffff;
  border-left: 1px solid #e5e7eb;
  padding: 20px 18px;
  overflow-y: auto;
  z-index: 40;
  box-shadow: -4px 0 16px rgba(0,0,0,0.04);

  @media (max-width: 1280px) {
    display: none;
  }
`

const ARSetSection = styled.div`
  margin-bottom: 18px;

  .label {
    font-size: 12.5px;
    font-weight: 700;
    color: #475569;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .sublabel {
    font-size: 11.5px;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 6px;
  }
`

const ARSetTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 14px;

  .help { color: #94a3b8; cursor: help; font-size: 13px; }
`

const ModelChip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;

  .thumb {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: ${p => p.$image ? `url(${p.$image}) center/cover` : '#e5e7eb'};
    border: 2px solid #c4b5fd;
    flex-shrink: 0;
  }
  .name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .change {
    padding: 5px 10px;
    border-radius: 7px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #475569;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    &:hover { border-color: #7c3aed; color: #7c3aed; }
  }
`

const XYZRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
`

const NumField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  color: #64748b;
  font-weight: 600;

  .head { display: flex; align-items: center; justify-content: space-between; }
  input {
    width: 100%;
    padding: 7px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 600;
    color: #0f172a;
    text-align: right;
    &:focus {
      outline: none;
      border-color: #7c3aed;
      box-shadow: 0 0 0 2px rgba(124,58,237,0.10);
    }
  }
`

const ResetBtn = styled.button`
  width: 100%;
  margin-top: 8px;
  padding: 7px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #64748b;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  &:hover { border-color: #7c3aed; color: #7c3aed; }
`

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  input[type="range"] {
    flex: 1;
    height: 4px;
    accent-color: #7c3aed;
  }
  .value {
    flex-shrink: 0;
    min-width: 50px;
    text-align: right;
    padding: 4px 8px;
    background: #f8fafc;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    color: #0f172a;
  }
`

const AnimSelect = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12.5px;
  background: #ffffff;
  color: #0f172a;
  cursor: pointer;
  &:focus { outline: none; border-color: #7c3aed; }
`

const HelpInfoBox = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  background: linear-gradient(135deg, rgba(124,58,237,0.06), rgba(37,99,235,0.06));
  border: 1px solid rgba(124,58,237,0.18);
  border-radius: 10px;
  font-size: 11.5px;
  color: #475569;
  line-height: 1.45;

  .head {
    display: flex; align-items: center; gap: 6px;
    color: #7c3aed; font-weight: 700; margin-bottom: 4px;
  }
`

export default function CreateQuizPage() {
  // 퀴즈 생성/편집 페이지 - 다중 문제 지원
  const { theme } = useTheme()
  const { isLoggedIn, user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { id: setId } = useParams() // URL에서 set_id 가져오기

  // 편집 모드 여부 확인
  const isEditMode = location.pathname.includes('/create/edit/')
  
  // 임시 저장 키
  const TEMP_SAVE_KEY = `quiz_temp_save_${user?.id || 'guest'}`
  
  // 이전 페이지에서 전달받은 데이터 (생성 모드)
  let initialData = {
    title: '',
    description: '',
    isPrivate: false,
    selectedObject: null,
    selectedObjects: [],
    activeTab: '',
    selectedCategory: null
  }
  
  // 새로고침으로 인해 location.state가 없는 경우 localStorage에서 복원
  if (!location.state && !isEditMode) {
    try {
      const savedData = localStorage.getItem(TEMP_SAVE_KEY)
      if (savedData) {
        const parsedData = JSON.parse(savedData)
        initialData = { ...initialData, ...parsedData }
        console.log('🔄 임시 저장된 데이터 복원:', parsedData)
      }
    } catch (error) {
      console.error('임시 저장 데이터 복원 실패:', error)
    }
  } else if (location.state) {
    initialData = { ...initialData, ...location.state }
  }
  
  const {
    title = initialData.title,
    description = initialData.description,
    isPrivate = initialData.isPrivate,
    questionId = initialData.questionId,
    selectedObject = initialData.selectedObject,
    selectedObjects = initialData.selectedObjects,
    activeTab = initialData.activeTab,
    selectedCategory = initialData.selectedCategory,
    questions: excelQuestions = initialData.questions, // Excel/CSV에서 로드된 문제 데이터
    isFromExcel = initialData.isFromExcel || false, // Excel에서 왔는지 플래그
    coverImage = initialData.coverImage || null, // 커버 이미지
    excelDataByObject = initialData.excelDataByObject || null, // 오브젝트별 Excel 데이터
    currentExcelObjectId = initialData.currentExcelObjectId || null, // 현재 처리중인 오브젝트 ID
    currentObjectIndex: initialObjectIndex = initialData.currentObjectIndex // CreateDetail에서 전달받은 인덱스
  } = initialData

  // Edit 모드 상태 (먼저 선언)
  const [isLoading, setIsLoading] = useState(isEditMode)
  const [editData, setEditData] = useState(null)

  // Title과 Description state 추가 (edit 모드에서 수정 가능하도록)
  const [titleState, setTitleState] = useState(title)
  const [descriptionState, setDescriptionState] = useState(description)
  const [isPrivateState, setIsPrivateState] = useState(isPrivate)

  // 기존 코드와의 호환성을 위해 변수 재할당
  const currentTitle = isEditMode ? titleState : title
  const currentDescription = isEditMode ? descriptionState : description
  const currentIsPrivate = isEditMode ? isPrivateState : isPrivate

  // 현재 퀴즈 세트에 포함된 객체들 관리
  const [quizObjects, setQuizObjects] = useState(selectedObjects.length > 0 ? selectedObjects : selectedObject ? [selectedObject] : [])

  // Edit 모드: 현재 보고 있는 오브젝트 인덱스 (CreateDetail에서 전달받은 값 사용)
  const [currentObjectIndex, setCurrentObjectIndex] = useState(initialObjectIndex !== undefined ? initialObjectIndex : 0)

  // === AR 요소 설정 (오브젝트별로 저장, 클라이언트 state) ===
  const [arSettings, setArSettings] = useState({}) // { [objectKey]: { x, y, z, scale, rotY, animation, speed } }
  const getArSetting = (idx) => {
    const key = idx
    return arSettings[key] || { x: 0, y: 0, z: 0, scale: 100, rotY: 0, animation: 'float', speed: 1.0 }
  }
  const updateArSetting = (idx, patch) => {
    setArSettings(prev => ({
      ...prev,
      [idx]: { ...getArSetting(idx), ...patch },
    }))
  }
  const resetArPosition = (idx) => updateArSetting(idx, { x: 0, y: 0, z: 0 })
  const resetArScale = (idx) => updateArSetting(idx, { scale: 100 })
  const resetArRotation = (idx) => updateArSetting(idx, { rotY: 0 })

  // 객체 전환 (이전/다음)
  const goToPrevObject = () => {
    if (currentObjectIndex > 0) setCurrentObjectIndex(currentObjectIndex - 1)
  }
  const goToNextObject = () => {
    if (currentObjectIndex < quizObjects.length - 1) setCurrentObjectIndex(currentObjectIndex + 1)
  }

  // 현재 객체 정보 표시 함수 ("000외 n개" 형태)
  const getCurrentObjectDisplayText = () => {
    if (isEditMode && quizObjects.length > 0) {
      // Edit 모드: 현재 오브젝트 이름 표시
      return `${quizObjects[currentObjectIndex]?.name || '알 수 없음'} (${currentObjectIndex + 1}/${quizObjects.length})`
    }
    if (quizObjects.length === 0) return '객체를 선택해주세요'
    if (quizObjects.length === 1) return quizObjects[0].name
    return `${quizObjects[0].name} 외 ${quizObjects.length - 1}개`
  }

  // 현재 표시할 객체
  const currentDisplayObject = (() => {
    if (quizObjects.length > 0 && currentObjectIndex >= 0 && currentObjectIndex < quizObjects.length) {
      // currentObjectIndex에 해당하는 오브젝트 표시 (Edit/생성 모드 공통)
      return quizObjects[currentObjectIndex]
    }
    // fallback: selectedObject 반환
    return selectedObject
  })()
  
  const getActiveObject = () => {
    if (isEditMode) {
      return quizObjects[currentObjectIndex] || null
    }
    // 생성 모드에서도 currentObjectIndex에 해당하는 물체 반환
    if (quizObjects.length > 0 && currentObjectIndex >= 0 && currentObjectIndex < quizObjects.length) {
      return quizObjects[currentObjectIndex]
    }
    return selectedObject
  }

  const getObjectId = (obj) => {
    if (!obj) return null
    return obj.object_id ?? obj.id ?? obj.ar_object_id ?? null
  }

  const toObjectKey = (value) => {
    if (value === null || value === undefined) return ''
    return String(value)
  }

  const getObjectKey = (obj) => toObjectKey(getObjectId(obj))

  const generateQuestionId = () => `question_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

  const createEmptyQuestion = (assignedObjectId = null) => ({
    id: generateQuestionId(),
    timeLimit: 20,
    question: '',
    choices: ['', '', '', ''],
    choiceTypes: [false, false, false, false],
    attachments: [],
    isAttachmentOpen: false,
    assignedObjectId,
    question_score: null, // 점수 (null이면 시간 기반)
  })

  const isQuestionEmpty = (question) => {
    if (!question) return true
    const hasPrompt = typeof question.question === 'string' && question.question.trim().length > 0
    const hasChoice = Array.isArray(question.choices) && question.choices.some(choice => (choice || '').trim().length > 0)
    const hasAttachments = Array.isArray(question.attachments) && question.attachments.length > 0
    return !hasPrompt && !hasChoice && !hasAttachments
  }

  const deriveChoiceTypes = (correctValue, choiceCount) => {
    if (!choiceCount || choiceCount <= 0) return Array(choiceCount).fill(false)

    let rawValues = []
    if (Array.isArray(correctValue)) {
      rawValues = correctValue
    } else if (typeof correctValue === 'string') {
      rawValues = correctValue.split(/[,\\s]+/).filter(Boolean)
    } else if (typeof correctValue === 'number') {
      rawValues = [correctValue]
    } else if (correctValue != null) {
      rawValues = [correctValue]
    }

    const numericValues = rawValues
      .map((val) => {
        const num = Number(val)
        return Number.isFinite(num) ? num : null
      })
      .filter((num) => num !== null)

    if (numericValues.length === 0) {
      const flags = Array(choiceCount).fill(false)
      if (typeof correctValue === 'number' && correctValue >= 0 && correctValue < choiceCount) {
        flags[correctValue] = true
      }
      return flags
    }

    const hasOneBased = numericValues.some((num) => num >= 1 && num <= choiceCount)
    const normalized = numericValues
      .map((num) => (hasOneBased ? num - 1 : num))
      .filter((idx) => idx >= 0 && idx < choiceCount)

    const unique = Array.from(new Set(normalized))
    const flags = Array(choiceCount).fill(false)
    unique.forEach((idx) => {
      flags[idx] = true
    })
    return flags
  }
  
  // 임시 저장된 questions 복원
  const getInitialQuestions = () => {
    // selectedObject에서 objectId 가져오기 (안전하게)
    const getObjectIdSafe = (obj) => {
      if (!obj) return null
      return obj.object_id ?? obj.id ?? obj.ar_object_id ?? null
    }

    const defaultObjectId = getObjectIdSafe(selectedObject)

    // 1. Excel/CSV 데이터가 있는 경우 우선 처리
    // CreateDetailPage에서 이미 변환된 문제 객체를 전달받음
    if (excelQuestions && Array.isArray(excelQuestions) && excelQuestions.length > 0) {
      // 첫 번째 항목을 확인해서 이미 변환된 객체인지 원본 CSV 데이터인지 판단
      const firstItem = excelQuestions[0]
      const isAlreadyConverted = firstItem && typeof firstItem.question === 'string'

      if (isAlreadyConverted) {
        // 이미 변환된 문제 객체 - CreateDetailPage에서 전달받은 경우
        console.log(`✅ 변환된 Excel 데이터 수신: ${excelQuestions.length}개 문제`)
        console.log(`  - 첫 번째 문제: "${excelQuestions[0].question.substring(0, 30)}..."`)

        // excelDataByObject 설정 (CreateDetailPage에서 전달된 것 사용)
        if (!initialData.excelDataByObject && initialData.currentExcelObjectId) {
          // currentExcelObjectId가 있으면 해당 오브젝트의 데이터로 설정
          initialData.excelDataByObject = {
            [initialData.currentExcelObjectId]: excelQuestions
          }
        }

        // 모든 문제 반환 (해당 오브젝트의 모든 문제를 표시)
        return excelQuestions
      } else {
        // 원본 CSV 데이터 - 변환 필요 (이전 방식 유지)
        console.log('📊 Excel/CSV 데이터를 문제로 변환:', excelQuestions)

        const questionsByObject = {}
        excelQuestions.forEach((item, index) => {
          const objectNum = String(item['오브젝트번호'] || item.오브젝트번호 || '1')
          if (!questionsByObject[objectNum]) {
            questionsByObject[objectNum] = []
          }

          const correctAnswerIndex = parseInt(item['정답번호'] || item.정답번호 || '1') - 1
          const choiceTypes = [false, false, false, false]
          if (correctAnswerIndex >= 0 && correctAnswerIndex < 4) {
            choiceTypes[correctAnswerIndex] = true
          }

          // 문제번호를 숫자로 파싱
          const questionNum = item['문제번호'] || item.문제번호
          const parsedQuestionNum = questionNum ? parseInt(questionNum) : (index + 1)

          questionsByObject[objectNum].push({
            id: `excel_question_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 8)}`,
            timeLimit: 20,
            question: item['문제내용'] || item.문제내용 || '',
            choices: [
              item['선택지1'] || item.선택지1 || '',
              item['선택지2'] || item.선택지2 || '',
              item['선택지3'] || item.선택지3 || '',
              item['선택지4'] || item.선택지4 || ''
            ],
            choiceTypes: choiceTypes,
            attachments: [],
            isAttachmentOpen: false,
            assignedObjectId: objectNum,
            originalQuestionNum: parsedQuestionNum
          })
        })

        const sortedObjectIds = Object.keys(questionsByObject).sort((a, b) => {
          const numA = parseInt(a)
          const numB = parseInt(b)
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB
          }
          return a.localeCompare(b)
        })

        console.log('📊 오브젝트별 문제 수:')
        sortedObjectIds.forEach(objectId => {
          console.log(`  - 오브젝트 ${objectId}: ${questionsByObject[objectId].length}개 문제`)
        })

        if (!initialData.excelDataByObject) {
          initialData.excelDataByObject = questionsByObject
        }

        const targetObjectId = initialData.currentExcelObjectId || sortedObjectIds[0]
        if (!initialData.currentExcelObjectId) {
          initialData.currentExcelObjectId = targetObjectId
        }

        const targetObjectQuestions = questionsByObject[targetObjectId]
        if (targetObjectQuestions && targetObjectQuestions.length > 0) {
          console.log(`✅ 오브젝트 ${targetObjectId}의 첫 번째 문제 로드`)
          console.log(`  - 문제: "${targetObjectQuestions[0].question.substring(0, 30)}..."`)
          console.log(`  - 남은 문제: ${targetObjectQuestions.length - 1}개`)
          return [targetObjectQuestions[0]]
        }
      }
    }

    // 2. 임시 저장된 데이터 복원
    if (!isEditMode && !location.state) {
      try {
        const savedData = localStorage.getItem(TEMP_SAVE_KEY)
        if (savedData) {
          const parsedData = JSON.parse(savedData)
          if (parsedData.questions && Array.isArray(parsedData.questions)) {
            const normalized = parsedData.questions.map((question) => ({
              ...question,
              assignedObjectId: question.assignedObjectId ?? defaultObjectId ?? null,
            }))
            if (normalized.length > 0) return normalized
          }
        }
      } catch (error) {
        console.error('임시 저장된 questions 복원 실패:', error)
      }
    }

    // 3. 기본값: 빈 문제 생성
    return [createEmptyQuestion(defaultObjectId ?? null)]
  }

  // 상태 관리
  const [questions, setQuestions] = useState(getInitialQuestions())
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [isSaveClicked, setIsSaveClicked] = useState(false)
  const [showMathModal, setShowMathModal] = useState(false)
  const [mathEquation, setMathEquation] = useState('')

  // Excel 데이터 관리 상태
  // getInitialQuestions에서 생성된 excelDataByObject를 사용
  const [excelDataByObjectState, setExcelDataByObjectState] = useState(() => {
    return initialData.excelDataByObject || excelDataByObject
  })
  const [currentExcelObjectIdState, setCurrentExcelObjectIdState] = useState(() => {
    return initialData.currentExcelObjectId || currentExcelObjectId
  })
  // getInitialQuestions에서 모든 문제를 로드했으므로 전체 길이로 시작
  const [loadedExcelQuestionCount, setLoadedExcelQuestionCount] = useState(
    (excelQuestions && excelQuestions.length > 0) ? excelQuestions.length : 0
  )

  // questionId가 변경될 때마다 저장 플래그 리셋 (CreateDetail에서 돌아올 때)
  useEffect(() => {
    console.log('🔄 CreateQuizPage 플래그 리셋 - questionId:', questionId);
    setIsSaveClicked(false);
  }, [questionId]);

  // ⚠️ 중복 useEffect 제거됨 - 아래 useEffect (라인 1718+)에서 처리
  // currentObjectIndex가 변경될 때 해당 객체의 문제를 로드하는 로직은
  // prevObjectIndexRef를 사용하는 useEffect에서 더 정확하게 처리됨

  // Excel 데이터 디버그 로그
  useEffect(() => {
    if (excelDataByObjectState) {
      console.log('📊 CreateQuizPage Excel 상태 초기화:')
      console.log('  - excelDataByObjectState:', Object.keys(excelDataByObjectState))
      console.log('  - currentExcelObjectIdState:', currentExcelObjectIdState)
      console.log('  - questions.length:', questions.length)
      if (currentExcelObjectIdState && excelDataByObjectState[currentExcelObjectIdState]) {
        console.log(`  - 오브젝트 ${currentExcelObjectIdState} 문제 수:`, excelDataByObjectState[currentExcelObjectIdState].length)
      }
    }
  }, []) // 초기 마운트 시에만 실행

  // 퀴즈 데이터 다시 불러오기 함수 (편집 완료 후 재로드용)
  const reloadQuizData = useCallback(async () => {
    if (!setId) return

    try {
      setIsLoading(true)

      // ★ 먼저 모든 상태 초기화 (꼬임 방지)
      setQuizObjects([])
      setQuestions([])
      setCurrentQuestionIndex(0)
      setCurrentObjectIndex(0)  // ★ 객체 인덱스도 0으로 리셋
      setEditData(null)

      // ★ ants_qstn_dtl 테이블을 사용하는 /get-quiz-detail API 사용 (메인 데이터 소스)
      const response = await fetch(`/auth/get-quiz-detail/${setId}`, {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()
      if (data.success) {
        console.log('✅ 퀴즈 데이터 새로 로드:', data.quiz)

        // editData 설정
        setEditData({
          id: data.quiz.set_id,
          title: data.quiz.set_title,
          description: data.quiz.set_description || '',
          isPrivate: data.quiz.is_private || false,
          problems: data.quiz.problems || []
        })

        // Title, Description, IsPrivate 상태 업데이트
        setTitleState(data.quiz.set_title || '')
        setDescriptionState(data.quiz.set_description || '')
        setIsPrivateState(data.quiz.is_private || false)

        // AR 객체별로 문제 그룹화
        const problemsByObject = {}
        data.quiz.problems.forEach(problem => {
          const objectId = problem.ar_object_id
          const objectName = problem.ar_object_name || '알 수 없는 객체'
          const objectImage = problem.ar_object_image || '/ThumbnailEx1.jpg'

          if (!problemsByObject[objectId]) {
            problemsByObject[objectId] = {
              id: objectId,
              name: objectName,
              image: objectImage,
              questions: []
            }
          }

          // 선택지 배열 구성 (항상 4개 슬롯 유지, 빈 값도 편집 가능하도록)
          const rawChoices = problem.choices || []
          const choices = [...rawChoices, '', '', '', ''].slice(0, 4)

          // choiceTypes 설정 (정답인 선택지는 true)
          const choiceTypes = Array(4).fill(false)
          if (Array.isArray(problem.correct_answer)) {
            problem.correct_answer.forEach(idx => {
              if (idx >= 0 && idx < choices.length) {
                choiceTypes[idx] = true
              }
            })
          }

          problemsByObject[objectId].questions.push({
            id: problem.question_id,
            question: problem.question_text,
            timeLimit: problem.question_timelimit || problem.timelimit || 20,
            question_score: problem.question_score || null,
            choices: choices,
            choiceTypes: choiceTypes,
            attachments: [],
            isAttachmentOpen: false,
            assignedObjectId: objectId  // ★ API 로드 시에도 assignedObjectId 설정
          })
        })

        // quizObjects 설정
        const objectsArray = Object.values(problemsByObject)

        // ★ 디버그: 각 객체별 문제 수 확인
        console.log('🔍 [DEBUG] 객체별 문제 그룹화 결과:')
        objectsArray.forEach((obj, idx) => {
          console.log(`  - [${idx}] ${obj.name} (id:${obj.id}): ${obj.questions.length}개 문제`)
          obj.questions.forEach((q, qIdx) => {
            console.log(`      문제${qIdx+1}: ${q.question?.substring(0, 30)}...`)
          })
        })

        setQuizObjects(objectsArray)

        // 첫 번째 객체의 문제들을 questions로 설정하고 인덱스 초기화
        // ★ 깊은 복사로 참조 문제 방지
        if (objectsArray.length > 0 && objectsArray[0].questions.length > 0) {
          const copiedQuestions = objectsArray[0].questions.map(q => ({ ...q }))
          console.log(`🔍 [DEBUG] 첫 번째 객체(${objectsArray[0].name})의 ${copiedQuestions.length}개 문제 로드 (복사본)`)
          setQuestions(copiedQuestions)
          setCurrentQuestionIndex(0)
        }
      }
    } catch (error) {
      console.error('퀴즈 데이터 로드 실패:', error)
      alert('퀴즈 데이터를 불러오는데 실패했습니다.')
    } finally {
      setIsLoading(false)
    }
  }, [setId])

  // Edit 모드: 기존 데이터 불러오기
  useEffect(() => {
    if (isEditMode && setId) {
      reloadQuizData()
    }
  }, [isEditMode, setId, reloadQuizData])

  // currentObjectIndex 변경 시 해당 객체의 questions 자동 로드 (인덱스가 실제로 변경된 경우에만)
  const prevObjectIndexRef = useRef(currentObjectIndex)
  useEffect(() => {
    console.log(`🔍 [DEBUG] useEffect 실행 - prevIdx:${prevObjectIndexRef.current}, currentIdx:${currentObjectIndex}, quizObjects.length:${quizObjects.length}`)

    // 인덱스가 실제로 변경된 경우에만 실행 (quizObjects 변경으로 인한 재실행 방지)
    if (prevObjectIndexRef.current !== currentObjectIndex) {
      prevObjectIndexRef.current = currentObjectIndex
      if (quizObjects.length > 0 && quizObjects[currentObjectIndex]?.questions) {
        // ★ 깊은 복사로 참조 문제 방지
        const originalQuestions = quizObjects[currentObjectIndex].questions
        const newQuestions = originalQuestions.map(q => ({ ...q }))
        const objName = quizObjects[currentObjectIndex]?.name || 'unknown'
        console.log(`🔍 [DEBUG] 객체 전환: ${objName} - ${newQuestions.length}개 문제 (원본: ${originalQuestions.length}개)`)
        newQuestions.forEach((q, i) => {
          console.log(`  문제${i+1}: ${q.question?.substring(0, 30)}...`)
        })
        setQuestions(newQuestions)
        setCurrentQuestionIndex(0)
        console.log(`📦 객체 ${currentObjectIndex} 문제 로드: ${newQuestions.length}개`)
      }
    }
  }, [currentObjectIndex, quizObjects])

  // 자동 저장 기능 (편집 모드가 아닐 때만)
  useEffect(() => {
    if (!isEditMode && user?.id) {
      const saveToLocalStorage = () => {
        try {
          const dataToSave = {
            title,
            description,
            isPrivate,
            selectedObject,
            selectedObjects,
            activeTab,
            selectedCategory,
            quizObjects,
            questions,
            currentQuestionIndex,
            timestamp: new Date().toISOString()
          }
          localStorage.setItem(TEMP_SAVE_KEY, JSON.stringify(dataToSave))
          console.log('💾 자동 저장 완료:', new Date().toLocaleTimeString())
        } catch (error) {
          console.error('자동 저장 실패:', error)
        }
      }

      const timeoutId = setTimeout(saveToLocalStorage, 1000) // 1초 후 저장
      return () => clearTimeout(timeoutId)
    }
  }, [title, description, isPrivate, selectedObject, selectedObjects, activeTab, selectedCategory, quizObjects, questions, currentQuestionIndex, isEditMode, user?.id, TEMP_SAVE_KEY])

  // 새로고침 방지 경고
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isEditMode && questions.some(q => q.question.trim() !== '' || q.choices.some(c => c.trim() !== ''))) {
        e.preventDefault()
        e.returnValue = '작성 중인 내용이 있습니다. 정말로 페이지를 떠나시겠습니까?'
        return e.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [questions, isEditMode])
  
  // 편집 모드에서 기존 데이터 로드 (위에서 이미 구현됨 - 중복 제거)

  useEffect(() => {
    // 로그인 체크
    if (!isLoggedIn) {
      alert('로그인이 필요한 페이지입니다.')
      navigate('/login')
      return
    }
    
    // 생성 모드에서만 필수 데이터 체크
    if (!isEditMode && !selectedObject) {
      alert('AR 객체가 선택되지 않았습니다.')
      navigate('/createdetail')
      return
    }
  }, [isLoggedIn, selectedObject, navigate, isEditMode])
  
  // 현재 문제 데이터 가져오기
  const getCurrentQuestion = () => questions[currentQuestionIndex]
  
  // 문제 업데이트 헬퍼
  // ★ 현재 questions가 활성 객체의 questions와 일치하는지 확인 후 업데이트
  const updateQuestionsForActiveObject = (updatedQuestions) => {
    const activeObject = getActiveObject()
    const activeObjectId = getObjectId(activeObject)

    // ★ 안전 검사: 업데이트하려는 questions가 현재 객체의 것인지 확인
    // (객체 전환 중에 이전 객체의 questions로 새 객체를 업데이트하는 것을 방지)
    if (activeObject && activeObjectId !== null && updatedQuestions.length > 0) {
      const firstQuestionObjectId = updatedQuestions[0]?.assignedObjectId
      // assignedObjectId가 있고 activeObjectId와 다르면 업데이트 스킵
      if (firstQuestionObjectId !== undefined && firstQuestionObjectId !== null &&
          firstQuestionObjectId !== activeObjectId) {
        console.log(`⚠️ [DEBUG] 객체 불일치로 업데이트 스킵: questions의 객체(${firstQuestionObjectId}) != 활성 객체(${activeObjectId})`)
        return
      }
    }

    if (activeObject && activeObjectId !== null) {
      // ★ quizObjects에 저장할 때 깊은 복사 (questions 상태와 참조 공유 방지)
      const copiedForStorage = updatedQuestions.map(q => ({ ...q }))
      setQuizObjects(prev =>
        prev.map(obj => {
          const objId = getObjectId(obj)
          if (objId === activeObjectId) {
            return {
              ...obj,
              questions: copiedForStorage,
              questionCount: copiedForStorage.length,
            }
          }
          return obj
        })
      )
    }

    setQuestions(updatedQuestions)
  }

  const updateCurrentQuestion = (updates) => {
    const updatedQuestions = questions.map((q, index) => {
      if (index !== currentQuestionIndex) return q
      const assignedObjectId = q.assignedObjectId ?? getObjectId(getActiveObject())
      return { ...q, ...updates, assignedObjectId }
    })
    updateQuestionsForActiveObject(updatedQuestions)
  }
  
  // 문제 추가
  const addQuestion = () => {
    const assignedObjectId = getObjectId(getActiveObject())

    // Excel 데이터가 있고, 현재 오브젝트에 아직 로드하지 않은 문제가 있으면 불러오기
    if (excelDataByObjectState && currentExcelObjectIdState) {
      const currentObjectQuestions = excelDataByObjectState[currentExcelObjectIdState]
      // 현재 questions.length가 로드된 문제의 개수
      const currentLoadedCount = questions.length

      console.log(`🔍 Excel 문제 로드 체크:`)
      console.log(`  - 현재 오브젝트: ${currentExcelObjectIdState}`)
      console.log(`  - 현재 로드된 문제 수: ${currentLoadedCount}`)
      console.log(`  - 총 문제 수: ${currentObjectQuestions ? currentObjectQuestions.length : 0}`)

      if (currentObjectQuestions && currentLoadedCount < currentObjectQuestions.length) {
        // 다음 문제 로드
        const nextQuestion = currentObjectQuestions[currentLoadedCount]
        console.log(`➕ Excel 데이터에서 오브젝트 ${currentExcelObjectIdState}의 ${currentLoadedCount + 1}번째 문제 로드`)
        console.log(`  - 문제: "${nextQuestion.question.substring(0, 30)}..."`)
        console.log(`  - 남은 문제: ${currentObjectQuestions.length - currentLoadedCount - 1}개`)

        const updatedQuestions = [...questions, nextQuestion]
        updateQuestionsForActiveObject(updatedQuestions)
        setCurrentQuestionIndex(updatedQuestions.length - 1)
        return
      } else {
        console.log(`⚠️ 오브젝트 ${currentExcelObjectIdState}의 모든 문제를 로드했습니다. 빈 문제를 추가합니다.`)
      }
    } else {
      console.log(`⚠️ Excel 데이터가 없습니다. excelDataByObjectState: ${excelDataByObjectState}, currentExcelObjectIdState: ${currentExcelObjectIdState}`)
    }

    // Excel 데이터가 없거나 모두 로드한 경우 빈 문제 추가
    const newQuestion = createEmptyQuestion(assignedObjectId)
    const updatedQuestions = [...questions, newQuestion]
    updateQuestionsForActiveObject(updatedQuestions)
    setCurrentQuestionIndex(updatedQuestions.length - 1)
  }
  
  // 문제 삭제
  const removeQuestion = (indexToRemove) => {
    if (questions.length <= 1) return // 최소 하나는 유지

    const remainingQuestions = questions.filter((_, index) => index !== indexToRemove)
    const assignedObjectId = getObjectId(getActiveObject())
    const ensuredQuestions = remainingQuestions.length > 0 ? remainingQuestions : [createEmptyQuestion(assignedObjectId)]
    updateQuestionsForActiveObject(ensuredQuestions)

    // 현재 인덱스 조정
    if (currentQuestionIndex >= ensuredQuestions.length) {
      setCurrentQuestionIndex(ensuredQuestions.length - 1)
    } else if (currentQuestionIndex > indexToRemove) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  // 물체(AR 오브젝트) 삭제
  const removeObject = async () => {
    if (quizObjects.length <= 1) {
      alert('최소 1개의 물체는 필요합니다.')
      return
    }

    const currentObject = getActiveObject()
    const objectName = currentObject?.name || '이 물체'

    if (!window.confirm(`"${objectName}"와 해당 물체의 모든 문제를 삭제하시겠습니까?`)) {
      return
    }

    // 현재 물체 삭제
    const updatedQuizObjects = quizObjects.filter((_, index) => index !== currentObjectIndex)

    // 인덱스 조정
    let newIndex = currentObjectIndex
    if (newIndex >= updatedQuizObjects.length) {
      newIndex = updatedQuizObjects.length - 1
    }

    setQuizObjects(updatedQuizObjects)
    setCurrentObjectIndex(newIndex)

    // 새 현재 물체의 문제들로 업데이트 (★ 깊은 복사로 참조 문제 방지)
    const newCurrentObject = updatedQuizObjects[newIndex]
    if (newCurrentObject?.questions) {
      const copiedQuestions = newCurrentObject.questions.map(q => ({ ...q }))
      setQuestions(copiedQuestions)
      setCurrentQuestionIndex(0)
    }

    console.log(`🗑️ 물체 "${objectName}" 삭제됨. 남은 물체: ${updatedQuizObjects.length}개`)
  }

  const handleChoiceChange = (index, value) => {
    const currentQuestion = getCurrentQuestion()
    const newChoices = [...currentQuestion.choices]
    newChoices[index] = value
    updateCurrentQuestion({ choices: newChoices })
  }
  
  const handleQuestionObjectChange = (questionIndex, targetKey) => {
    console.log(`🔄 [DEBUG] handleQuestionObjectChange 호출! questionIndex=${questionIndex}, targetKey=${targetKey}`)
    if (!isEditMode) return

    const currentObject = getActiveObject()
    const currentObjectId = getObjectId(currentObject)
    console.log(`🔄 [DEBUG] 현재 객체: ${currentObject?.name} (id:${currentObjectId})`)
    if (!currentObject || currentObjectId === null) {
      alert('선택된 AR 객체가 없습니다.')
      return
    }

    const targetObject = quizObjects.find(obj => getObjectKey(obj) === targetKey)
    if (!targetObject) {
      alert('선택한 AR 객체를 찾을 수 없습니다.')
      return
    }

    const targetObjectId = getObjectId(targetObject)
    if (targetObjectId === null || targetObjectId === currentObjectId) return

    const questionToMove = questions[questionIndex]
    if (!questionToMove) return

    // ★ 남은 문제들도 깊은 복사 (참조 문제 방지)
    const remainingQuestions = questions.filter((_, idx) => idx !== questionIndex).map(q => ({ ...q }))
    const ensuredRemaining = remainingQuestions.length > 0 ? remainingQuestions : [createEmptyQuestion(currentObjectId)]

    // ★ 기존 대상 객체의 questions도 깊은 복사 (참조 문제 방지)
    const existingTargetQuestions = (targetObject.questions || []).map(q => ({ ...q }))
    const baseTargetQuestions =
      existingTargetQuestions.length === 1 && isQuestionEmpty(existingTargetQuestions[0])
        ? []
        : existingTargetQuestions
    const targetQuestions = [...baseTargetQuestions, { ...questionToMove, assignedObjectId: targetObjectId }]

    const updatedQuizObjects = quizObjects.map(obj => {
      const objId = getObjectId(obj)
      if (objId === currentObjectId) {
        return {
          ...obj,
          questions: ensuredRemaining,
          questionCount: ensuredRemaining.length,
        }
      }
      if (objId === targetObjectId) {
        return {
          ...obj,
          questions: targetQuestions,
          questionCount: targetQuestions.length,
        }
      }
      return obj
    })

    setQuizObjects(updatedQuizObjects)

    const targetIndex = updatedQuizObjects.findIndex(obj => getObjectId(obj) === targetObjectId)
    if (targetIndex >= 0) {
      setCurrentObjectIndex(targetIndex)
      // ★ 깊은 복사로 참조 문제 방지
      setQuestions(targetQuestions.map(q => ({ ...q })))
      setCurrentQuestionIndex(Math.max(targetQuestions.length - 1, 0))
    } else {
      // ★ 깊은 복사로 참조 문제 방지
      setQuestions(ensuredRemaining.map(q => ({ ...q })))
      setCurrentQuestionIndex(Math.min(currentQuestionIndex, ensuredRemaining.length - 1))
    }
  }


  const handleChoiceTypeChange = (index) => {
    const currentQuestion = getCurrentQuestion()
    const newChoiceTypes = [...currentQuestion.choiceTypes]
    
    // 복수 정답 지원 (최대 2개까지)
    if (!newChoiceTypes[index]) {
      // 현재 정답 개수 확인
      const currentCorrectCount = newChoiceTypes.filter(type => type).length
      
      if (currentCorrectCount >= 2) {
        alert('정답은 최대 2개까지만 선택할 수 있습니다.')
        return
      }
      
      // 정답으로 설정
      newChoiceTypes[index] = true
    } else {
      // 정답 해제
      newChoiceTypes[index] = false
    }
    
    updateCurrentQuestion({ choiceTypes: newChoiceTypes })
  }
  
  // 첨부파일 삭제 함수
  const removeAttachment = (attachmentIndex) => {
    const currentQuestion = getCurrentQuestion()
    const updatedAttachments = currentQuestion.attachments.filter((_, index) => index !== attachmentIndex)
    updateCurrentQuestion({ 
      attachments: updatedAttachments,
      isAttachmentOpen: updatedAttachments.length > 0 // 첨부파일이 없으면 접기
    })
    console.log(`첨부파일 ${attachmentIndex} 삭제됨. 남은 파일: ${updatedAttachments.length}개`)
  }

  // 전체 첨부파일 용량 계산 함수
  const calculateTotalAttachmentSize = (currentAttachments = [], newAttachment = null) => {
    let totalSize = 0;
    
    // 기존 첨부파일들의 크기 합계
    currentAttachments.forEach(att => {
      if (att.size) {
        totalSize += att.size;
      } else if (att.url && att.url.startsWith('data:')) {
        // Base64 데이터의 경우 대략적인 크기 계산
        const base64Length = att.url.split(',')[1]?.length || 0;
        totalSize += Math.round(base64Length * 0.75); // Base64는 실제 크기의 약 133%
      }
    });
    
    // 새로 추가될 첨부파일 크기 추가
    if (newAttachment && newAttachment.size) {
      totalSize += newAttachment.size;
    }
    
    return totalSize;
  };

  // 첨부파일 용량 체크 함수
  const checkAttachmentSizeLimit = (currentAttachments, newFileSize, fileName) => {
    const currentTotalSize = calculateTotalAttachmentSize(currentAttachments);
    const newTotalSize = currentTotalSize + newFileSize;
    const maxTotalSize = 20 * 1024 * 1024; // 20MB 제한

    const currentTotalMB = (currentTotalSize / (1024 * 1024)).toFixed(2);
    const newTotalMB = (newTotalSize / (1024 * 1024)).toFixed(2);
    const maxTotalMB = (maxTotalSize / (1024 * 1024)).toFixed(0);

    if (newTotalSize > maxTotalSize) {
      alert(`전체 첨부파일 용량이 제한을 초과합니다.\n\n현재 총 용량: ${currentTotalMB}MB\n추가할 파일: ${fileName} (${(newFileSize / (1024 * 1024)).toFixed(2)}MB)\n예상 총 용량: ${newTotalMB}MB\n최대 허용 용량: ${maxTotalMB}MB\n\n기존 첨부파일을 삭제하거나 더 작은 파일을 업로드해주세요.`);
      return false;
    }

    return true;
  };

  // file.skynet.re.kr로 파일 업로드 함수
  const uploadToSkynet = async (file) => {
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadId', uploadId);

    try {
      const response = await axios.post('/ObjectUpload/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const uploadedUrl = response.data?.data?.data?.files?.[0]?.url;

      if (uploadedUrl) {
        console.log('✅ 파일 업로드 성공:', uploadedUrl);
        return uploadedUrl;
      } else {
        throw new Error('업로드된 URL을 받지 못했습니다.');
      }
    } catch (error) {
      console.error('❌ 파일 업로드 실패:', error);
      throw error;
    }
  };

  const handleIconClick = (iconType) => {
    console.log('아이콘 클릭:', iconType)
    const currentQuestion = getCurrentQuestion()
    
    // 각 아이콘별 기능 구현
    switch (iconType) {
      case 'picture':
        // 이미지 업로드 기능 (file.skynet.re.kr로 업로드)
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.onchange = async (e) => {
          const file = e.target.files[0]
          if (file) {
            // 파일 크기 체크 (5MB = 5 * 1024 * 1024 bytes)
            const maxSize = 5 * 1024 * 1024;
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

            if (file.size > maxSize) {
              alert(`이미지 파일이 너무 큽니다.\n현재 크기: ${fileSizeMB}MB\n최대 허용 크기: 5MB\n\n파일 크기를 줄여서 다시 업로드해주세요.`);
              return;
            }

            // 전체 첨부파일 용량 체크
            if (!checkAttachmentSizeLimit(currentQuestion.attachments, file.size, file.name)) {
              return;
            }

            console.log(`📷 이미지 업로드 시작: ${file.name} (${fileSizeMB}MB)`);

            try {
              // file.skynet.re.kr로 업로드
              const uploadedUrl = await uploadToSkynet(file);

              const newAttachment = {
                type: 'image',
                url: uploadedUrl, // URL로 저장
                name: file.name,
                size: file.size
              }
              updateCurrentQuestion({
                attachments: [...currentQuestion.attachments, newAttachment],
                isAttachmentOpen: true
              })

              console.log(`✅ 이미지 업로드 완료: ${uploadedUrl}`);
            } catch (error) {
              alert(`이미지 업로드에 실패했습니다: ${error.message}`);
              console.error('이미지 업로드 오류:', error);
            }
          }
        }
        input.click()
        break
      case 'audio':
        // 음성 업로드 기능 (file.skynet.re.kr로 업로드)
        const audioInput = document.createElement('input')
        audioInput.type = 'file'
        audioInput.accept = 'audio/*'
        audioInput.onchange = async (e) => {
          const file = e.target.files[0]
          if (file) {
            // 파일 크기 체크 (10MB = 10 * 1024 * 1024 bytes)
            const maxSize = 10 * 1024 * 1024;
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

            if (file.size > maxSize) {
              alert(`음성 파일이 너무 큽니다.\n현재 크기: ${fileSizeMB}MB\n최대 허용 크기: 10MB\n\n파일 크기를 줄이거나 압축해서 다시 업로드해주세요.`);
              return;
            }

            // 전체 첨부파일 용량 체크
            if (!checkAttachmentSizeLimit(currentQuestion.attachments, file.size, file.name)) {
              return;
            }

            console.log(`🎵 음성 업로드 시작: ${file.name} (${fileSizeMB}MB)`);

            try {
              // file.skynet.re.kr로 업로드
              const uploadedUrl = await uploadToSkynet(file);

              const newAttachment = {
                type: 'audio',
                url: uploadedUrl, // URL로 저장
                name: file.name,
                size: file.size
              }
              updateCurrentQuestion({
                attachments: [...currentQuestion.attachments, newAttachment],
                isAttachmentOpen: true
              })

              console.log(`✅ 음성 업로드 완료: ${uploadedUrl}`);
            } catch (error) {
              alert(`음성 업로드에 실패했습니다: ${error.message}`);
              console.error('음성 업로드 오류:', error);
            }
          }
        }
        audioInput.click()
        break
      case 'math':
        // 수식 입력 모달 열기
        setShowMathModal(true)
        break
    }
  }

  // Edit 모드: 이전/다음 오브젝트로 이동
  // ★ 객체 전환 전에 현재 질문을 명시적으로 저장 (인덱스 변경 전에 저장해야 함)
  const saveQuestionsToCurrentObject = () => {
    const currentObjId = getObjectId(quizObjects[currentObjectIndex])
    if (currentObjId === null) return

    // 현재 questions를 현재 객체에 저장 (깊은 복사)
    const savedQuestions = questions.map(q => ({ ...q }))
    console.log(`💾 [DEBUG] 객체 ${currentObjectIndex}에 ${savedQuestions.length}개 질문 저장`)

    setQuizObjects(prev =>
      prev.map((obj, idx) => {
        if (idx === currentObjectIndex) {
          return { ...obj, questions: savedQuestions, questionCount: savedQuestions.length }
        }
        return obj
      })
    )
  }

  const handlePreviousObject = () => {
    if (currentObjectIndex > 0) {
      const newIndex = currentObjectIndex - 1
      console.log(`🔙 이전 객체로 이동: ${currentObjectIndex} → ${newIndex}`)
      // ★ 먼저 현재 객체에 질문 저장
      saveQuestionsToCurrentObject()
      setCurrentObjectIndex(newIndex)
      // useEffect가 새 객체의 questions 로드를 처리함
    }
  }

  const handleNextObjectInEdit = () => {
    if (currentObjectIndex < quizObjects.length - 1) {
      const newIndex = currentObjectIndex + 1
      console.log(`🔜 다음 객체로 이동: ${currentObjectIndex} → ${newIndex}`)
      // ★ 먼저 현재 객체에 질문 저장
      saveQuestionsToCurrentObject()
      setCurrentObjectIndex(newIndex)
      // useEffect가 새 객체의 questions 로드를 처리함
    }
  }

  // ★ 드롭다운에서 객체 선택 시 해당 객체로 보기 전환 (문제 이동 없이)
  const handleObjectViewChange = (targetKey) => {
    const targetIndex = quizObjects.findIndex(obj => getObjectKey(obj) === targetKey)
    if (targetIndex < 0 || targetIndex === currentObjectIndex) return

    console.log(`👁️ 객체 보기 전환: ${currentObjectIndex} → ${targetIndex}`)
    // 먼저 현재 객체에 질문 저장
    saveQuestionsToCurrentObject()
    // 선택한 객체로 이동
    setCurrentObjectIndex(targetIndex)
    // useEffect가 새 객체의 questions 로드를 처리함
  }

  // 다음 AR 객체 추가를 위해 CreateDetail로 이동
  const handleAddNextObject = async () => {
    // 중복 저장 방지
    if (isSaveClicked) {
      console.log('⚠️ 이미 저장 중입니다. 중복 클릭 무시.');
      return;
    }

    setIsSaveClicked(true);

    // 현재 퀴즈 검증
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i]
      if (!q.question.trim()) {
        alert(`문제 ${i + 1}번의 내용을 입력해주세요.`)
        setCurrentQuestionIndex(i)
        setIsSaveClicked(false);
        return
      }

      console.log(`🔍 문제 ${i + 1} 검증:`, {
        choices: q.choices,
        choicesTypes: q.choices.map(c => typeof c),
        choicesValues: q.choices.map(c => `"${c}"`),
      });

      // 숫자도 문자열로 변환하여 처리 (엑셀에서 날짜가 숫자로 파싱되는 경우 대응)
      const filledChoices = q.choices.filter(choice => {
        if (!choice && choice !== 0) return false; // null, undefined 제거 (0은 유효)
        const strChoice = String(choice).trim();
        return strChoice.length > 0;
      })

      console.log(`✅ 문제 ${i + 1} 필터링된 선택지:`, filledChoices);

      if (filledChoices.length < 2) {
        console.log(`❌ 문제 ${i + 1} 선택지 부족: ${filledChoices.length}개`);
        alert(`문제 ${i + 1}번의 선택지를 최소 2개 입력해주세요.`)
        setCurrentQuestionIndex(i)
        setIsSaveClicked(false);
        return
      }

      const correctCount = q.choiceTypes.filter(type => type).length
      if (correctCount === 0) {
        alert(`문제 ${i + 1}번의 정답을 선택해주세요.`)
        setCurrentQuestionIndex(i)
        setIsSaveClicked(false);
        return
      }
    }
    
    try {
      const activeObject = getActiveObject()
      if (!activeObject) {
        alert('선택된 AR 객체가 없습니다.')
        setIsSaveClicked(false);
        return
      }

      const activeObjectId = activeObject.object_id || activeObject.id
      if (!activeObjectId) {
        alert('AR 객체 ID가 없습니다.')
        setIsSaveClicked(false);
        return
      }

      // 첫 번째 객체인 경우 퀴즈를 먼저 생성 또는 업데이트
      let savedQuizId = questionId || editData?.id || setId; // 기존 ID가 있으면 사용

      console.log('🔍 savedQuizId 확인:', savedQuizId);
      console.log('🔍 questionId 확인:', questionId);

      if (!savedQuizId) {
        console.log('🆕 첫 번째 객체: 퀴즈 생성 (questionId 없음)');
      } else {
        console.log('🔄 첫 번째 객체: 기존 퀴즈 업데이트 (questionId 있음)');
      }

      // 퀴즈 메타데이터만 준비 (문제는 나중에 별도로 저장)
      const metadataBody = {
        title: currentTitle,
        description: currentDescription,
        isPrivate: currentIsPrivate,
        selectedObject: activeObject,
        questions: [],  // 빈 배열로 보내서 메타데이터만 저장
        userId: user.id,
        setId: savedQuizId,      // set_id 전달 (UPDATE용)
        questionId: savedQuizId  // 호환성을 위해 questionId도 전달
      };

      console.log('📝 퀴즈 메타데이터만 저장 (문제는 별도 저장)');
      console.log('📤 물체추가 - isPrivate 값:', currentIsPrivate, ', is_shared should be:', !currentIsPrivate);

      // 퀴즈 메타데이터만 생성 또는 업데이트 API 호출
      const response = await fetch('/auth/save-complete-quiz', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(metadataBody),
        });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      const result = await response.json();

      if (result.success) {
        savedQuizId = result.quiz.setId || result.quiz.set_id || result.quiz.quiz_id;
        console.log('✅ 퀴즈 저장 완료, set_id:', savedQuizId);
      } else {
        throw new Error(result.error || '퀴즈 저장 실패');
      }

      if (savedQuizId) {
        // 기존 퀴즈에 현재 AR 객체의 문제들 저장
        console.log('📝 기존 퀴즈에 현재 AR 객체의 문제들 저장');
        
        // 현재 AR 객체의 문제들 저장 - AuthRouter.js API에 맞는 파라미터 구조
        const objectPayload = {
          setId: savedQuizId,              // AuthRouter.js에서 기대하는 파라미터명
          objectId: activeObjectId,        // AuthRouter.js에서 기대하는 파라미터명
          userId: user.id,                 // 사용자 ID 추가
          questions: questions.map(q => ({
            question: q.question,
            questionText: q.question, // 백워드 호환성
            choices: q.choices
              .filter(choice => {
                if (!choice && choice !== 0) return false;
                return String(choice).trim().length > 0;
              })
              .map(choice => String(choice)), // 숫자를 문자열로 변환
            choiceTypes: q.choiceTypes,
            correctAnswers: q.choiceTypes.map((isCorrect, idx) => isCorrect ? idx : null).filter(idx => idx !== null),
            timeLimit: q.timeLimit || 30,
            question_score: q.question_score || null,
            attachments: q.attachments || []
          }))
        };

        console.log('💾 현재 AR 객체 저장 요청:', {
          arObjectName: activeObject.name,
          arObjectId: activeObjectId,
          questionCount: questions.length
        });

        console.log('📋 전송할 questions 데이터 상세:', objectPayload.questions.map((q, idx) => ({
          index: idx,
          questionText: q.questionText,
          choices: q.choices,
          choiceTypes: q.choiceTypes,
          correctAnswers: q.correctAnswers,
          question_score: q.question_score
        })));

        console.log('🔍 원본 questions state:', questions.map((q, idx) => ({
          index: idx,
          choiceTypes: q.choiceTypes,
          choices: q.choices
        })));

        const saveResponse = await fetch('/auth/save-complete-quiz-data', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(objectPayload),
        });

        if (!saveResponse.ok) {
          throw new Error(`현재 AR 객체 저장 실패: ${saveResponse.status}: ${await saveResponse.text()}`);
        }

        const saveResult = await saveResponse.json();

        if (!saveResult.success) {
          throw new Error(saveResult.error || '현재 AR 객체 저장 실패');
        }

        console.log('✅ 현재 AR 객체 문제들 저장 완료 (ants_qstn_dtl)');
      }
      
      // 현재 AR 객체를 저장된 객체 목록에 추가 (중복 체크)
      const currentObjectInfo = {
        ...activeObject,
        questionCount: questions.length,
        isSaved: true,
        questions,
      };

      // 중복 체크: object_id 또는 id로 이미 존재하는지 확인
      const isAlreadyAdded = quizObjects.some(obj => getObjectId(obj) === activeObjectId);

      const updatedQuizObjects = isAlreadyAdded
        ? quizObjects.map(obj => {
            const objectId = getObjectId(obj)
            return objectId === activeObjectId
              ? currentObjectInfo  // 기존 객체 정보 업데이트
              : obj
          })
        : [...quizObjects, currentObjectInfo];  // 새 객체 추가

      if (isEditMode) {
        setQuizObjects(updatedQuizObjects)
      }

      console.log('🎯 저장된 객체 목록 업데이트:', updatedQuizObjects.map(obj => ({
        name: obj.name,
        object_id: obj.object_id || obj.id,
        questionCount: obj.questionCount,
        isNew: !isAlreadyAdded
      })));
      
      // CreateDetail로 이동하면서 선택된 객체들과 퀴즈 ID 전달
      navigate('/createdetail', {
        state: {
          title: currentTitle,
          description: currentDescription,
          isPrivate: currentIsPrivate,
          questionId: savedQuizId, // 생성된 퀴즈 ID 전달
          selectedObjects: updatedQuizObjects, // 현재까지 저장된 객체들
          currentObjectIndex: updatedQuizObjects.length - 1, // 마지막으로 작업한 객체 인덱스
          activeTab,
          selectedCategory,
          isAddingMore: true, // 추가 모드임을 표시
          selectedObject: getActiveObject(),
          // Excel 데이터 전달 (다음 오브젝트에서 사용)
          excelDataByObject: excelDataByObjectState,
          isFromExcel: isFromExcel,
          coverImage: coverImage
        }
      })

      // 네비게이션 성공 시에도 플래그 리셋 (다음 페이지 진입 전에 리셋)
      setIsSaveClicked(false);

    } catch (error) {
      console.error('💥 다음 객체 추가 중 오류:', error);
      alert('다음 객체 추가 중 오류가 발생했습니다: ' + error.message);
      setIsSaveClicked(false);
    }
  }

  // AR 객체와 모든 문제 데이터를 저장하는 함수
  const saveARObjectToQuestionDtl = async (quizSetId) => {
    const activeObject = getActiveObject();
    console.log('🚀 saveARObjectToQuestionDtl 함수 시작');
    console.log('📋 activeObject:', activeObject);
    console.log('👤 user:', user);
    console.log('❓ questions 길이:', questions.length);
    
    if (!activeObject) {
      console.log('⚠️ 선택된 AR 객체가 없습니다.');
      alert('선택된 AR 객체가 없습니다.');
      return;
    }

    if (!user || !user.id) {
      console.log('⚠️ 사용자 정보가 없습니다.');
      alert('사용자 정보가 없습니다. 다시 로그인 해주세요.');
      return;
    }

    const activeObjectId = getObjectId(activeObject);

    if (!activeObjectId) {
      console.log('⚠️ AR 객체 ID가 없습니다.');
      alert('AR 객체 ID가 없습니다.');
      return;
    }

    try {
      // 정답 번호 변환 함수 (배열 인덱스를 1,2,3,4로 변환)
      // 정답 인덱스를 0-based로 저장 (DB 기존 데이터와 일치)
      const getCorrectAnswerIndices = (choiceTypes) => {
        return choiceTypes
          .map((isCorrect, index) => isCorrect ? index : null)  // 0-based 인덱스
          .filter(idx => idx !== null);
      };

      // 각 문제별로 데이터 구조화
      const questionsData = questions.map((question, questionIndex) => {
        // 첨부파일 분류
        const attachmentsByType = {
          image: question.attachments?.filter(att => att.type === 'image') || [],
          audio: question.attachments?.filter(att => att.type === 'audio') || [],
          math: question.attachments?.filter(att => att.type === 'math' || att.type === 'equation') || []
        };

        // 선택지를 ants_answer 테이블 컬럼에 맞게 매핑 (숫자도 문자열로 변환)
        const filteredChoices = question.choices
          .filter(choice => {
            if (!choice && choice !== 0) return false;
            return String(choice).trim().length > 0;
          })
          .map(choice => String(choice));
        const answerData = {
          answer_text1: filteredChoices[0] || null,
          answer_text2: filteredChoices[1] || null, 
          answer_text3: filteredChoices[2] || null,
          answer_text4: filteredChoices[3] || null,
          correct_answer: getCorrectAnswerIndices(question.choiceTypes).join(',') // "0,2" 형태로 저장 (0-based)
        };

        return {
          questionText: question.question,
          timeLimit: question.timeLimit,
          // ants_answer 테이블용 데이터
          answers: answerData,
          // 첨부파일을 데이터베이스 컬럼명에 맞게 전송
          attachments: {
            attached_img: attachmentsByType.image.map(att => ({
              name: att.name,
              url: att.url,
              type: 'image'
            })),
            attached_voice: attachmentsByType.audio.map(att => ({
              name: att.name,
              url: att.url,
              type: 'audio'
            })),
            attached_formula: attachmentsByType.math.map(att => ({
              name: att.name,
              content: att.content || att.equation,
              type: 'math'
            }))
          }
        };
      });

      console.log('🔗 AR 객체와 문제 데이터 저장 시작:', {
        selectedObject: activeObject,
        selectedObjectId: activeObjectId,
        quizSetId,
        questionsCount: questionsData.length,
        questionsData
      });

      console.log('📤 전송할 데이터:', {
        objectId: activeObjectId,
        userId: user.id,
        questions: questionsData
      });

      const response = await fetch('/auth/save-complete-quiz-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          objectId: activeObjectId, // AR 객체 ID (ants_ar_objects.object_id로 ar_object_id 조회용)
          questions: questionsData, // 모든 문제 데이터
          setId: quizSetId // ants_question의 question_id (savedQuizId)
        }),
      });

      console.log('📡 응답 상태:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP 에러 응답:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ 전체 퀴즈 데이터 저장 성공:', result);
        console.log(`📊 저장된 데이터: AR 객체 1개, 문제 ${questionsData.length}개 저장 완료`);
      } else {
        console.error('❌ 퀴즈 데이터 저장 실패:', result);
        const errorMsg = `퀴즈 문제 저장에 실패했습니다:
에러: ${result.error}
상세: ${result.details || '상세 정보 없음'}
코드: ${result.errorCode || '코드 없음'}`;
        alert(errorMsg);
        throw new Error('퀴즈 데이터 저장 실패: ' + result.details);
      }
    } catch (error) {
      console.error('💥 퀴즈 데이터 저장 중 오류:', error);
      alert('퀴즈 문제 저장 중 오류가 발생했습니다: ' + error.message);
      throw error;
    }
  };

  const handleSave = async () => {
    // 중복 저장 방지
    if (isSaveClicked) {
      console.log('⚠️ 이미 저장 중입니다. 중복 클릭 무시.');
      return;
    }

    setIsSaveClicked(true);
    
    // 모든 문제 검증
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i]
      if (!q.question.trim()) {
        alert(`문제 ${i + 1}번의 내용을 입력해주세요.`)
        setCurrentQuestionIndex(i)
        setIsSaveClicked(false);
        return
      }

      const filledChoices = q.choices.filter(choice =>
        choice && typeof choice === 'string' && choice.trim()
      )
      if (filledChoices.length < 2) {
        alert(`문제 ${i + 1}번의 선택지를 최소 2개 입력해주세요.`)
        setCurrentQuestionIndex(i)
        setIsSaveClicked(false);
        return
      }

      const correctCount = q.choiceTypes.filter(type => type).length
      if (correctCount === 0) {
        alert(`문제 ${i + 1}번의 정답을 선택해주세요.`)
        setCurrentQuestionIndex(i)
        setIsSaveClicked(false);
        return
      }

      if (correctCount > 2) {
        alert(`문제 ${i + 1}번의 정답은 최대 2개까지만 선택할 수 있습니다.`)
        setCurrentQuestionIndex(i)
        setIsSaveClicked(false);
        return
      }
    }

    // Excel에서 업로드된 데이터를 처음 저장하는 경우
    // 각 오브젝트는 "물체 추가" 버튼으로 이미 저장되었으므로,
    // 마지막 오브젝트만 저장하면 됨
    if (isFromExcel && !isEditMode) {
      const activeObject = getActiveObject();
      const activeObjectId = getObjectId(activeObject);

      console.log('📊 Excel 퀴즈 최종 저장 시작 (마지막 오브젝트):', {
        title: currentTitle,
        description: currentDescription,
        isPrivate: currentIsPrivate,
        currentObjectQuestions: questions.length,
        activeObjectId,
        existingSavedQuizId: questionId || editData?.id || setId
      });

      try {
        if (!user || !user.id) {
          alert('로그인 정보가 없습니다. 다시 로그인해주세요.');
          setIsSaveClicked(false);
          navigate('/login');
          return;
        }

        // savedQuizId 확인 (물체 추가를 한 번도 안 눌렀을 경우 없을 수 있음)
        let finalQuizId = questionId || editData?.id || setId;

        if (!finalQuizId) {
          console.log('🆕 퀴즈 메타데이터 생성 (처음 저장)');

          const metadataBody = {
            title: currentTitle,
            description: currentDescription,
            isPrivate: currentIsPrivate,
            selectedObject: activeObject,
            questions: [],
            userId: user.id,
            coverImage: coverImage
          };

          const metadataResponse = await fetch('/auth/save-complete-quiz', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(metadataBody),
          });

          if (!metadataResponse.ok) {
            throw new Error(`퀴즈 메타데이터 생성 실패: ${metadataResponse.status}`);
          }

          const metadataResult = await metadataResponse.json();
          if (metadataResult.success) {
            finalQuizId = metadataResult.quiz.setId || metadataResult.quiz.set_id || metadataResult.quiz.quiz_id;
            console.log('✅ 퀴즈 메타데이터 생성 완료, set_id:', finalQuizId);
          } else {
            throw new Error(metadataResult.error || '퀴즈 메타데이터 생성 실패');
          }
        }

        // 마지막 오브젝트의 문제들을 저장
        console.log('💾 마지막 오브젝트 저장 시작...', { finalQuizId, activeObjectId });

        const saveResponse = await fetch('/auth/save-complete-quiz-data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            setId: finalQuizId,
            objectId: activeObjectId,
            questions: questions.map((q) => ({
              question: q.question,
              questionText: q.question,
              choices: q.choices,
              choiceTypes: q.choiceTypes,
              correctAnswers: q.choiceTypes.map((isCorrect, idx) => isCorrect ? idx : null).filter(idx => idx !== null),
              timeLimit: q.timeLimit || 20,
              question_score: q.question_score || null,
              attachments: q.attachments || []
            }))
          })
        });

        if (!saveResponse.ok) {
          const errorResult = await saveResponse.json();
          throw new Error(errorResult.error || '마지막 오브젝트 저장 실패');
        }

        const saveResult = await saveResponse.json();
        console.log('✅ 마지막 오브젝트 저장 완료:', saveResult);

        // 퀴즈 저장 완료
        const result = { success: true, quizId: finalQuizId };
        console.log('✅ Excel 퀴즈 저장 완료:', result);

        if (result.success) {
          console.log('📤 퀴즈 저장 완료 - isPrivate:', currentIsPrivate, ', is_shared should be:', !currentIsPrivate);

          // is_shared와 is_saved 상태 업데이트
          try {
            console.log('📤 is_shared 업데이트 시도:', {
              questionId: finalQuizId,
              isSaved: true,
              isShared: !currentIsPrivate
            });

            const updateResponse = await fetch('/auth/update-quiz-saved-status', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                questionId: finalQuizId,
                isSaved: true,
                isShared: !currentIsPrivate  // is_shared도 함께 업데이트 시도
              }),
            });

            if (updateResponse.ok) {
              const updateResult = await updateResponse.json();
              console.log('✅ 퀴즈 상태 업데이트 완료:', updateResult);
            } else {
              console.warn('⚠️ 퀴즈 상태 업데이트 실패:', updateResponse.status);
            }
          } catch (error) {
            console.error('💥 퀴즈 상태 업데이트 오류:', error);
          }

          // 임시 저장 데이터 삭제
          try {
            localStorage.removeItem(TEMP_SAVE_KEY);
            console.log('��️ 임시 저장 데이터 삭제 완료');
          } catch (error) {
            console.error('임시 저장 데이터 삭제 실패:', error);
          }

          alert('퀴즈가 성공적으로 저장되었습니다!');
          navigate(`/problem/${finalQuizId}`);
        } else {
          throw new Error(result.error || '저장 실패');
        }
      } catch (error) {
        console.error('💥 Excel 퀴즈 저장 오류:', error);
        alert('퀴즈 저장 중 오류가 발생했습니다: ' + error.message);
        setIsSaveClicked(false);
      }
      return; // Excel 저장 후 함수 종료
    }

    if (isEditMode) {
      console.log('🔄 퀴즈 수정 모드: 저장 시작:', {
        setId,
        title,
        description,
        questionsCount: questions.length
      })

      try {
        // 현재 AR 객체 정보
        const activeObject = getActiveObject();
        console.log('🔍 activeObject:', activeObject);
        console.log('🔍 quizObjects:', quizObjects);
        console.log('🔍 currentObjectIndex:', currentObjectIndex);
        console.log('🔍 selectedObject:', selectedObject);

        if (!activeObject) {
          alert('AR 객체 정보가 없습니다.');
          setIsSaveClicked(false);
          return;
        }

        const activeObjectId = activeObject.object_id || activeObject.id;
        console.log('🔍 activeObjectId:', activeObjectId);

        // 문제 데이터 변환
        const questionsData = questions.map(q => ({
          question: q.question,
          questionText: q.question,
          arObjectId: activeObjectId, // AR 객체 ID 추가
          choices: q.choices
            .filter(choice => {
              if (!choice && choice !== 0) return false;
              return String(choice).trim().length > 0;
            })
            .map(choice => String(choice)), // 숫자를 문자열로 변환
          choiceTypes: q.choiceTypes,
          correctAnswers: q.choiceTypes.map((isCorrect, idx) => isCorrect ? idx : null).filter(idx => idx !== null),
          timeLimit: q.timeLimit || 30,
          question_score: q.question_score || null,
          attachments: q.attachments || []
        }));

        // 한 번에 메타데이터와 문제 업데이트 (save-complete-quiz-data 사용 - ants_qstn_dtl)
        console.log('📤 퀴즈 전체 업데이트 요청:', {
          setId,
          objectId: activeObjectId,
          questionsCount: questionsData.length
        });

        const questionsResponse = await fetch('/auth/save-complete-quiz-data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            objectId: activeObjectId,
            questions: questionsData,
            setId: setId
          })
        });

        if (!questionsResponse.ok) {
          const errorText = await questionsResponse.text();
          throw new Error(`문제 업데이트 실패: ${questionsResponse.status} - ${errorText}`);
        }

        const result = await questionsResponse.json();

        // 메타데이터도 업데이트 (제목, 설명 변경 반영)
        // ★ selectedObject를 보내지 않으면 서버가 커버 이미지를 업데이트하지 않음
        const metadataResponse = await fetch('/auth/save-complete-quiz', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title,
            description,
            isPrivate,
            // selectedObject 제거: 편집 시 커버 이미지가 오브젝트 이미지로 바뀌지 않도록
            userId: user.id,
            setId: setId,
            questionId: setId,
            questions: [] // 메타데이터만 업데이트
          })
        });

        if (!metadataResponse.ok) {
          console.warn('⚠️ 메타데이터 업데이트 실패했지만 문제는 저장됨');
        }

        if (result.success) {
          alert('편집이 저장되었습니다.');
          // 페이지 새로고침으로 완전히 새로 불러오기
          window.location.reload();
        } else {
          throw new Error(result.error || '수정 실패');
        }
      } catch (error) {
        console.error('💥 퀴즈 수정 오류:', error);
        alert('퀴즈 수정 중 오류가 발생했습니다: ' + error.message);
        setIsSaveClicked(false);
      }
    } else {
      // 퀴즈 저장 - 물체추가와 동일한 두 단계 저장 프로세스 사용
      try {
        console.log('💾 CreateQuizPage: 퀴즈 완료 저장 시작');
        console.log('🆔 questionId:', questionId);
        console.log('👤 Current user from context:', JSON.stringify(user, null, 2));

        if (!user || !user.id) {
          alert('로그인 정보가 없습니다. 다시 로그인해주세요.');
          setIsSaveClicked(false);
          navigate('/login');
          return;
        }

        // 현재 AR 객체 정보 가져오기
        const activeObject = getActiveObject();
        if (!activeObject) {
          alert('선택된 AR 객체가 없습니다.');
          setIsSaveClicked(false);
          return;
        }

        const activeObjectId = activeObject.object_id || activeObject.id;
        if (!activeObjectId) {
          alert('AR 객체 ID가 없습니다.');
          setIsSaveClicked(false);
          return;
        }

        // 1단계: 퀴즈 메타데이터 저장 또는 업데이트
        let savedQuizId = questionId || editData?.id || setId;

        console.log('🔍 savedQuizId 확인:', savedQuizId);

        const metadataBody = {
          title: currentTitle,
          description: currentDescription,
          isPrivate: currentIsPrivate,
          selectedObject: activeObject,
          questions: [],  // 메타데이터만 저장
          userId: user.id,
          setId: savedQuizId,
          questionId: savedQuizId,
          coverImage: coverImage
        };

        console.log('📝 1단계: 퀴즈 메타데이터 저장');

        const metadataResponse = await fetch('/auth/save-complete-quiz', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(metadataBody),
        });

        if (!metadataResponse.ok) {
          throw new Error(`HTTP ${metadataResponse.status}: ${await metadataResponse.text()}`);
        }

        const metadataResult = await metadataResponse.json();

        if (metadataResult.success) {
          savedQuizId = metadataResult.quiz.setId || metadataResult.quiz.set_id || metadataResult.quiz.quiz_id;
          console.log('✅ 퀴즈 메타데이터 저장 완료, set_id:', savedQuizId);
        } else {
          throw new Error(metadataResult.error || '퀴즈 메타데이터 저장 실패');
        }

        // 2단계: AR 객체의 문제들 저장
        if (savedQuizId) {
          console.log('📝 2단계: AR 객체의 문제들 저��');

          const objectPayload = {
            setId: savedQuizId,
            objectId: activeObjectId,
            userId: user.id,
            questions: questions.map(q => ({
              question: q.question,
              questionText: q.question,
              choices: q.choices
                .filter(choice => {
                  if (!choice && choice !== 0) return false;
                  return String(choice).trim().length > 0;
                })
                .map(choice => String(choice)),
              choiceTypes: q.choiceTypes,
              correctAnswers: q.choiceTypes.map((isCorrect, idx) => isCorrect ? idx : null).filter(idx => idx !== null),
              timeLimit: q.timeLimit || 30,
              question_score: q.question_score || null,
              attachments: q.attachments || []
            }))
          };

          console.log('💾 AR 객체 저장 요청:', {
            arObjectName: activeObject.name,
            arObjectId: activeObjectId,
            questionCount: questions.length
          });

          const response = await fetch('/auth/save-complete-quiz-data', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(objectPayload),
          });

          console.log('📨 API Response status:', response.status);

          if (!response.ok) {
            const errorResult = await response.json();
            throw new Error(errorResult.error || 'AR 객체 저장 실패');
          }

          const result = await response.json();
          console.log('✅ AR 객체 저장 완료 (ants_qstn_dtl):', result);

          if (result.success) {
            // 임시 저장 데이터 삭제
            try {
              localStorage.removeItem(TEMP_SAVE_KEY);
              console.log('🗑️ 임시 저장 데이터 삭제 완료');
            } catch (error) {
              console.error('임시 저장 데이터 삭제 실패:', error);
            }

            // set_is_saved를 true로 업데이트
            try {
              const updateResponse = await fetch('/auth/update-quiz-saved-status', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                  questionId: savedQuizId,
                  isSaved: true
                }),
              });

              if (updateResponse.ok) {
                console.log('✅ 퀴즈 저장 상태 업데이트 완료');
              } else {
                console.warn('⚠️ 퀴즈 저장 상태 업데이트 실패');
              }
            } catch (error) {
              console.error('💥 퀴즈 저장 상태 업데이트 오류:', error);
            }

            alert(`퀴즈 세트 "${currentTitle}" 제작이 완료되었습니다!\n홈 화면으로 이동합니다.`);

            // 홈페이지로 이동
            navigate('/home', {
              state: {
                newQuizCreated: true,
                newQuizId: savedQuizId,
                newQuizTitle: currentTitle
              }
            });
          } else {
            throw new Error(result.error || '퀴즈 저장 실패');
          }
        } else {
          throw new Error('퀴즈 ID를 받지 못했습니다.');
        }
      } catch (error) {
        console.error('💥 퀴즈 저장 중 오류:', error);
        alert(`퀴즈 저장에 실패했습니다:\n${error.message}\n\n다시 시도해주세요.`);
        setIsSaveClicked(false);
      }
    }
  }

  // 수식 입력 모달 컴포넌트
  const MathModal = () => {
    const [selectedFormula, setSelectedFormula] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('')
    const [currentStep, setCurrentStep] = useState(1)
    const [values, setValues] = useState({})
    const [currentVariable, setCurrentVariable] = useState('')
    const [inputValue, setInputValue] = useState('')
    
    // 수식 템플릿들 (readcode의 HTML에서 가져온 전체 구조)
    const formulaCategories = {
      'basic': {
        title: '기본 사칙연산',
        formulas: {
          'addition': {
            display: '<span data-var="a">a</span> + <span data-var="b">b</span> = <span data-var="c">c</span>',
            variables: ['a', 'b', 'c'],
            title: 'a + b = c'
          },
          'subtraction': {
            display: '<span data-var="a">a</span> - <span data-var="b">b</span> = <span data-var="c">c</span>',
            variables: ['a', 'b', 'c'],
            title: 'a - b = c'
          },
          'multiplication': {
            display: '<span data-var="a">a</span> × <span data-var="b">b</span> = <span data-var="c">c</span>',
            variables: ['a', 'b', 'c'],
            title: 'a × b = c'
          },
          'division': {
            display: '<span data-var="a">a</span> ÷ <span data-var="b">b</span> = <span data-var="c">c</span>',
            variables: ['a', 'b', 'c'],
            title: 'a ÷ b = c'
          }
        }
      },
      'equations': {
        title: '방정식',
        formulas: {
          'linear': {
            display: '<span data-var="a">a</span>x + <span data-var="b">b</span> = 0',
            variables: ['a', 'b'],
            title: 'ax + b = 0'
          },
          'quadratic': {
            display: '<span data-var="a">a</span>x² + <span data-var="b">b</span>x + <span data-var="c">c</span> = 0',
            variables: ['a', 'b', 'c'],
            title: 'ax² + bx + c = 0'
          },
          'cubic': {
            display: '<span data-var="a">a</span>x³ + <span data-var="b">b</span>x² + <span data-var="c">c</span>x + <span data-var="d">d</span> = 0',
            variables: ['a', 'b', 'c', 'd'],
            title: 'ax³ + bx² + cx + d = 0'
          },
          'system2': {
            display: '<span data-var="a1">a₁</span>x + <span data-var="b1">b₁</span>y = <span data-var="c1">c₁</span><br/><span data-var="a2">a₂</span>x + <span data-var="b2">b₂</span>y = <span data-var="c2">c₂</span>',
            variables: ['a1', 'b1', 'c1', 'a2', 'b2', 'c2'],
            title: '연립방정식 (2원)'
          }
        }
      },
      'functions': {
        title: '함수',
        formulas: {
          'linear-func': {
            display: 'y = <span data-var="m">m</span>x + <span data-var="b">b</span>',
            variables: ['m', 'b'],
            title: 'y = mx + b'
          },
          'quadratic-func': {
            display: 'y = <span data-var="a">a</span>x² + <span data-var="b">b</span>x + <span data-var="c">c</span>',
            variables: ['a', 'b', 'c'],
            title: 'y = ax² + bx + c'
          },
          'exponential': {
            display: 'y = <span data-var="a">a</span>·<span data-var="b">b</span><sup>x</sup>',
            variables: ['a', 'b'],
            title: 'y = a·b^x'
          },
          'logarithmic': {
            display: 'y = <span data-var="a">a</span>·log(<span data-var="x">x</span>) + <span data-var="b">b</span>',
            variables: ['a', 'x', 'b'],
            title: 'y = a·log(x) + b'
          }
        }
      },
      'radicals': {
        title: '근호/지수',
        formulas: {
          'sqrt': {
            display: '<span style="position:relative;padding-left:20px;"><span style="position:absolute;left:0;font-size:1.2em;font-weight:bold;">√</span><span style="border-top:2px solid #2c3e50;padding-top:2px;"><span data-var="a">a</span>x + <span data-var="b">b</span></span></span>',
            variables: ['a', 'b'],
            title: '√(ax + b)'
          },
          'power': {
            display: '<span data-var="a">a</span><sup><span data-var="n">n</span></sup>',
            variables: ['a', 'n'],
            title: 'a^n'
          },
          'radical-eq': {
            display: '<span style="position:relative;padding-left:20px;"><span style="position:absolute;left:0;font-size:1.2em;font-weight:bold;">√</span><span style="border-top:2px solid #2c3e50;padding-top:2px;">x</span></span> = <span data-var="a">a</span>',
            variables: ['a'],
            title: '√x = a'
          },
          'power-eq': {
            display: 'x<sup><span data-var="n">n</span></sup> = <span data-var="a">a</span>',
            variables: ['n', 'a'],
            title: 'x^n = a'
          }
        }
      },
      'fractions': {
        title: '분수',
        formulas: {
          'simple-fraction': {
            display: '<div style="display:inline-block;vertical-align:middle;text-align:center;margin:0 4px;"><span data-var="a">a</span><div style="border-top:2px solid #2c3e50;margin:2px 0;"></div><span data-var="b">b</span></div>',
            variables: ['a', 'b'],
            title: 'a/b'
          },
          'complex-fraction': {
            display: '<div style="display:inline-block;vertical-align:middle;text-align:center;margin:0 4px;"><div><span data-var="a">a</span>x + <span data-var="b">b</span></div><div style="border-top:2px solid #2c3e50;margin:2px 0;"></div><div><span data-var="c">c</span>x + <span data-var="d">d</span></div></div>',
            variables: ['a', 'b', 'c', 'd'],
            title: '(ax+b)/(cx+d)'
          },
          'fraction-eq': {
            display: '<div style="display:inline-block;vertical-align:middle;text-align:center;margin:0 4px;"><span data-var="a">a</span><div style="border-top:2px solid #2c3e50;margin:2px 0;"></div>x</div> = <span data-var="b">b</span>',
            variables: ['a', 'b'],
            title: 'a/x = b'
          },
          'rational-eq': {
            display: '<div style="display:inline-block;vertical-align:middle;text-align:center;margin:0 4px;"><div><span data-var="a">a</span>x</div><div style="border-top:2px solid #2c3e50;margin:2px 0;"></div><div><span data-var="b">b</span>x + <span data-var="c">c</span></div></div> = <span data-var="d">d</span>',
            variables: ['a', 'b', 'c', 'd'],
            title: 'ax/(bx+c) = d'
          }
        }
      },
      'geometry': {
        title: '기하',
        formulas: {
          'circle': {
            display: 'x² + y² = <span data-var="r">r</span>²',
            variables: ['r'],
            title: 'x² + y² = r²'
          },
          'line-distance': {
            display: '<span style="position:relative;padding-left:20px;"><span style="position:absolute;left:0;font-size:1.2em;font-weight:bold;">√</span><span style="border-top:2px solid #2c3e50;padding-top:2px;">(<span data-var="x2">x₂</span> - <span data-var="x1">x₁</span>)² + (<span data-var="y2">y₂</span> - <span data-var="y1">y₁</span>)²</span></span>',
            variables: ['x1', 'y1', 'x2', 'y2'],
            title: '√((x₂-x₁)² + (y₂-y₁)²)'
          },
          'area-triangle': {
            display: '½ × <span data-var="base">밑변</span> × <span data-var="height">높이</span>',
            variables: ['base', 'height'],
            title: '½ × 밑변 × 높이'
          },
          'pythagorean': {
            display: '<span data-var="a">a</span>² + <span data-var="b">b</span>² = <span data-var="c">c</span>²',
            variables: ['a', 'b', 'c'],
            title: 'a² + b² = c²'
          }
        }
      },
      'trigonometry': {
        title: '삼각함수',
        formulas: {
          'sin': {
            display: 'y = <span data-var="a">a</span>·sin(<span data-var="b">b</span>x + <span data-var="c">c</span>)',
            variables: ['a', 'b', 'c'],
            title: 'y = a·sin(bx + c)'
          },
          'cos': {
            display: 'y = <span data-var="a">a</span>·cos(<span data-var="b">b</span>x + <span data-var="c">c</span>)',
            variables: ['a', 'b', 'c'],
            title: 'y = a·cos(bx + c)'
          },
          'tan': {
            display: 'y = <span data-var="a">a</span>·tan(<span data-var="b">b</span>x + <span data-var="c">c</span>)',
            variables: ['a', 'b', 'c'],
            title: 'y = a·tan(bx + c)'
          },
          'trig-identity': {
            display: 'sin²<span data-var="x">x</span> + cos²<span data-var="y">x</span> = 1',
            variables: ['x', 'y'],
            title: 'sin²x + cos²x = 1'
          }
        }
      }
    }

    // 모든 수식을 flat하게 만든 객체
    const allFormulas = {}
    Object.entries(formulaCategories).forEach(([categoryKey, category]) => {
      Object.entries(category.formulas).forEach(([formulaKey, formula]) => {
        allFormulas[formulaKey] = formula
      })
    })

    const handleFormulaSelect = (formula) => {
      setSelectedFormula(formula)
      setCurrentStep(2)
      setValues({})
      if (allFormulas[formula]?.variables.length > 0) {
        setCurrentVariable(allFormulas[formula].variables[0])
        setInputValue('')
      }
    }

    const updateValue = (variable, value) => {
      setValues(prev => ({...prev, [variable]: value}))
    }

    const moveToNextVariable = () => {
      const variables = allFormulas[selectedFormula]?.variables
      if (!variables) return
      
      const currentIndex = variables.indexOf(currentVariable)
      const nextIndex = (currentIndex + 1) % variables.length
      setCurrentVariable(variables[nextIndex])
      setInputValue(values[variables[nextIndex]] || '')
    }

    const handleQuickNumber = (value) => {
      updateValue(currentVariable, value)
      setInputValue(value)
      setTimeout(moveToNextVariable, 200)
    }

    const handleToggleValue = (value) => {
      const currentValue = values[currentVariable] || ''
      const isNegative = currentValue.startsWith('(-') && currentValue.endsWith(')')
      
      if (isNegative) {
        // 음수에서 양수로
        updateValue(currentVariable, value)
        setInputValue(value)
      } else {
        // 양수에서 음수로 (괄호 포함)
        const negativeValue = '(-' + value + ')'
        updateValue(currentVariable, negativeValue)
        setInputValue(negativeValue)
      }
    }

    const handleConfirm = () => {
      const formula = allFormulas[selectedFormula]
      if (!formula) return

      let equationText = formula.display
      
      // HTML에서 실제 텍스트 생성
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = equationText
      
      // data-var 속성을 가진 span들을 찾아서 값으로 치환
      const spans = tempDiv.querySelectorAll('span[data-var]')
      spans.forEach(span => {
        const variable = span.getAttribute('data-var')
        const value = values[variable] || variable
        span.textContent = value
      })
      
      const finalEquation = tempDiv.innerHTML
      
      const newAttachment = {
        type: 'equation',
        equation: tempDiv.textContent || tempDiv.innerText,
        formula: selectedFormula,
        values: values,
        name: `수식: ${tempDiv.textContent || tempDiv.innerText}`
      }
      
      const currentQuestion = getCurrentQuestion()
      updateCurrentQuestion({ 
        attachments: [...currentQuestion.attachments, newAttachment],
        isAttachmentOpen: true
      })
      setShowMathModal(false)
      
      // 상태 초기화
      setSelectedFormula('')
      setCurrentStep(1)
      setValues({})
      setCurrentVariable('')
      setInputValue('')
    }

    const handleClear = () => {
      setValues({})
      if (allFormulas[selectedFormula]?.variables.length > 0) {
        setCurrentVariable(allFormulas[selectedFormula].variables[0])
        setInputValue('')
      }
    }

    const handleClose = () => {
      setShowMathModal(false)
      setSelectedFormula('')
      setCurrentStep(1)
      setValues({})
      setCurrentVariable('')
      setInputValue('')
    }

    const goBack = () => {
      setCurrentStep(1)
      setSelectedFormula('')
      setValues({})
      setCurrentVariable('')
      setInputValue('')
    }

    const renderEquation = () => {
      if (!selectedFormula) return null
      
      const formula = allFormulas[selectedFormula]
      let display = formula.display
      
      // HTML 문자열을 파싱하여 변수들을 스타일링된 요소로 변경
      const parser = new DOMParser()
      const doc = parser.parseFromString(`<div>${display}</div>`, 'text/html')
      const spans = doc.querySelectorAll('span[data-var]')
      
      spans.forEach(span => {
        const variable = span.getAttribute('data-var')
        const value = values[variable]
        const isActive = currentVariable === variable
        const isFilled = value !== undefined && value !== ''
        
        span.className = `coefficient ${isActive ? 'active' : isFilled ? 'filled' : 'empty'}`
        span.textContent = value || variable
        span.style.cursor = 'pointer'
        span.setAttribute('data-variable', variable)
      })
      
      return doc.body.firstChild.innerHTML
    }

    return (
      <MathModalOverlay onClick={handleClose}>
        <MathModalContainer onClick={(e) => e.stopPropagation()}>
          <MathModalHeader>
            <h2>수학 수식 입력기</h2>
            <p>{currentStep === 1 ? '수식 형태를 선택하세요' : '값을 입력해보세요'}</p>
          </MathModalHeader>

          {/* 단계 표시 */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '10px', 
            margin: '20px 0',
            padding: '0 20px' 
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '20px',
              background: currentStep === 1 ? '#4facfe' : '#28a745',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.3)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>1</div>
              <span>수식 선택</span>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: '20px',
              background: currentStep === 2 ? '#4facfe' : '#f8f9fa',
              color: currentStep === 2 ? 'white' : '#6c757d',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: currentStep === 2 ? 'rgba(255,255,255,0.3)' : 'currentColor',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>2</div>
              <span>값 입력</span>
            </div>
          </div>

          {currentStep === 1 ? (
            <div style={{ padding: '20px' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '15px' 
              }}>
                {Object.entries(formulaCategories).map(([categoryKey, category]) => (
                  <div key={categoryKey} style={{
                    border: '2px solid #e9ecef',
                    borderRadius: '12px',
                    padding: '15px',
                    background: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#4facfe'
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 172, 254, 0.2)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e9ecef'
                    e.currentTarget.style.boxShadow = 'none'
                  }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '10px',
                      color: '#2c3e50'
                    }}>
                      {category.title}
                    </div>
                    
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {Object.entries(category.formulas).map(([formulaKey, formula]) => (
                        <button
                          key={formulaKey}
                          onClick={() => handleFormulaSelect(formulaKey)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px',
                            background: 'white',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontSize: '14px',
                            textAlign: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#4facfe'
                            e.target.style.color = 'white'
                            e.target.style.borderColor = '#4facfe'
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'white'
                            e.target.style.color = 'inherit'
                            e.target.style.borderColor = '#e9ecef'
                          }}
                        >
                          {formula.title}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <EquationDisplay>
                <div 
                  style={{ fontSize: '28px', fontWeight: '500', color: '#2c3e50', lineHeight: 1.6, fontFamily: 'Times New Roman, serif' }}
                  dangerouslySetInnerHTML={{ __html: renderEquation() }}
                  onClick={(e) => {
                    const variable = e.target.getAttribute('data-variable')
                    if (variable) {
                      setCurrentVariable(variable)
                      setInputValue(values[variable] || '')
                    }
                  }}
                />
                
                <ProgressIndicator>
                  {allFormulas[selectedFormula]?.variables.map(variable => (
                    <ProgressDot 
                      key={variable}
                      className={
                        currentVariable === variable ? 'active' : 
                        values[variable] ? 'filled' : ''
                      }
                    />
                  ))}
                </ProgressIndicator>
              </EquationDisplay>

              <InputSection>
                <CoefficientTabs>
                  {allFormulas[selectedFormula]?.variables.map(variable => (
                    <Tab 
                      key={variable}
                      className={
                        currentVariable === variable ? 'active' : 
                        values[variable] ? 'filled' : ''
                      }
                      onClick={() => {
                        setCurrentVariable(variable)
                        setInputValue(values[variable] || '')
                      }}
                    >
                      {variable}
                    </Tab>
                  ))}
                </CoefficientTabs>

                <CurrentInput>
                  <InputLabel>변수 <strong>{currentVariable}</strong> 값을 입력하세요</InputLabel>
                  <MathInputField
                    type="text"
                    placeholder="숫자나 문자를 입력하세요"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value)
                      updateValue(currentVariable, e.target.value)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        moveToNextVariable()
                      }
                    }}
                  />
                </CurrentInput>

                <QuickNumbers>
                  {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0.5', '0.333', '0.25', 'x', 'y', 'a', 'b', '다음→'].map(value => (
                    value === '다음→' ? (
                      <QuickBtn 
                        key={value} 
                        onClick={moveToNextVariable}
                        style={{ background: '#28a745', color: 'white', borderColor: '#28a745' }}
                      >
                        {value}
                      </QuickBtn>
                    ) : ['x', 'y', 'a', 'b', '1', '2', '3', '4'].includes(value) ? (
                      <QuickBtn 
                        key={value} 
                        onClick={() => handleToggleValue(value)}
                        style={{
                          background: values[currentVariable] === `(-${value})` ? '#dc3545' : 'white',
                          color: values[currentVariable] === `(-${value})` ? 'white' : 'inherit',
                          borderColor: values[currentVariable] === `(-${value})` ? '#dc3545' : '#dee2e6'
                        }}
                      >
                        {values[currentVariable] === `(-${value})` ? `-${value === '0.5' ? '½' : value === '0.333' ? '⅓' : value === '0.25' ? '¼' : value}` : 
                         value === '0.5' ? '½' : value === '0.333' ? '⅓' : value === '0.25' ? '¼' : value}
                      </QuickBtn>
                    ) : (
                      <QuickBtn key={value} onClick={() => handleQuickNumber(value)}>
                        {value === '0.5' ? '½' : value === '0.333' ? '⅓' : value === '0.25' ? '¼' : value}
                      </QuickBtn>
                    )
                  ))}
                </QuickNumbers>

                <ActionButtons>
                  <BtnCancel onClick={goBack}>뒤로가기</BtnCancel>
                  <BtnClear onClick={handleClear}>지우기</BtnClear>
                  <BtnConfirm onClick={handleConfirm}>확인</BtnConfirm>
                </ActionButtons>
              </InputSection>
            </>
          )}
        </MathModalContainer>
      </MathModalOverlay>
    )
  }

  // 편집 모드일 때 로딩 체크
  if (isEditMode && (isLoading || !editData)) {
    return (
      <Container>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          fontSize: '18px',
          color: '#666'
        }}>
          {isLoading ? '문제 데이터를 불러오는 중...' : '문제를 찾을 수 없습니다.'}
        </div>
      </Container>
    )
  }

  // 생성 모드일 때 selectedObject 체크
  if (!isEditMode && !selectedObject) {
    return null
  }

  // ★ 디버그: 렌더링 시 questions 상태 확인
  console.log(`🔍 [DEBUG] 렌더링 - questions.length: ${questions.length}, currentObjectIndex: ${currentObjectIndex}`)

  return (
    <>
      <TopBar>
        <BackBtn onClick={() => navigate(-1)}>← 만들기로 돌아가기</BackBtn>
        <StepperWrap>
          {[
            { n: 1, label: '기본 정보' },
            { n: 2, label: '오브젝트 등록' },
            { n: 3, label: '문제 만들기', active: true },
            { n: 4, label: '배포하기' },
          ].map((s, i, arr) => (
            <React.Fragment key={s.n}>
              <StepDot $active={s.active}>
                <span className="num">{s.n}</span>
                <span className="label">{s.label}</span>
              </StepDot>
              {i < arr.length - 1 && <StepLine />}
            </React.Fragment>
          ))}
        </StepperWrap>
        <TopActions>
          {/* 객체 전환은 좌측 ObjectBadge + 하단 NavButton에서 처리 */}
          <SaveDraftBtn onClick={() => navigate(-1)}>← 이전 단계</SaveDraftBtn>
          <SaveDraftBtn onClick={handleSave}>📌 임시저장</SaveDraftBtn>
          <NextStepBtn onClick={handleSave}>다음 단계 →</NextStepBtn>
        </TopActions>
      </TopBar>
    <Container>
      {/* 데스크톱 레이아웃 (681px 이상) */}
      <LeftSection>
        {/* 퀴즈 제목 + 편집 중 객체 한 줄 표시 */}
        {title && (
          <QuizTitleHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <QuizTitleText style={{ margin: 0 }}>퀴즈 제목</QuizTitleText>
              <QuizTitle style={{ margin: 0 }}>{title}</QuizTitle>
              {questionId && <QuizId style={{ margin: 0 }}>ID: {questionId}</QuizId>}
              {quizObjects.length > 0 && quizObjects[currentObjectIndex] && (
                <ObjectBadge title={quizObjects[currentObjectIndex].name} style={{ margin: 0, marginLeft: 'auto' }}>
                  <span className="label">편집 중</span>
                  <span className="name">{quizObjects[currentObjectIndex].name}</span>
                  {quizObjects.length > 1 && (
                    <span className="index">{currentObjectIndex + 1} / {quizObjects.length}</span>
                  )}
                </ObjectBadge>
              )}
            </div>
          </QuizTitleHeader>
        )}
        
        <DesktopHeader>
          <DesktopSectionTitle>{isEditMode ? '퀴즈 편집' : '퀴즈 만들기'}</DesktopSectionTitle>
          <CompleteButton onClick={handleSave}>
            {isEditMode ? '수정 완료' : '퀴즈 완료'}
          </CompleteButton>
        </DesktopHeader>
        
        {/* 문제 네비게이션 */}
        <div style={{ padding: '0 20px' }}>
          <QuestionNavigationContainer>
            <QuestionTabs>
              {questions.map((q, index) => (
                <QuestionTab
                  key={q.id}
                  $isActive={index === currentQuestionIndex}
                  $hasContent={q.question.trim() !== ''}
                  onClick={() => setCurrentQuestionIndex(index)}
                >
                  {index + 1}
                </QuestionTab>
              ))}
              <AddQuestionTabButton onClick={addQuestion}>+</AddQuestionTabButton>
            </QuestionTabs>
            
            <QuestionInfo>
              <QuestionMeta>
                <span>문제 {currentQuestionIndex + 1} / {questions.length}</span>
                {isEditMode && quizObjects.length > 0 && (
                  <ObjectSelectWrapper>
                    <ObjectSelectLabel>객체</ObjectSelectLabel>
                    <ObjectSelect
                      value={getObjectKey(quizObjects[currentObjectIndex])}
                      onChange={(e) => handleObjectViewChange(e.target.value)}
                    >
                      {quizObjects.map((obj, idx) => {
                        const value = getObjectKey(obj)
                        return (
                          <option key={value || idx} value={value}>
                            {obj.name || `객체 ${idx + 1}`}
                          </option>
                        )
                      })}
                    </ObjectSelect>
                  </ObjectSelectWrapper>
                )}
              </QuestionMeta>
              <QuestionActions>
                <AddQuestionButton onClick={addQuestion}>문제 추가</AddQuestionButton>
                {questions.length > 1 && (
                  <DeleteQuestionButton 
                    onClick={() => removeQuestion(currentQuestionIndex)}
                    disabled={questions.length <= 1}
                  >
                    문제 삭제
                  </DeleteQuestionButton>
                )}
              </QuestionActions>
            </QuestionInfo>
          </QuestionNavigationContainer>
        </div>

        {/* 시간제한 + 점수 한 줄 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <DesktopTimeSection>
            <TimeLabel>시간제한</TimeLabel>
            <TimeInputGroup>
              <DesktopTimeInput
                type="number"
                value={getCurrentQuestion().timeLimit}
                onChange={(e) => updateCurrentQuestion({ timeLimit: parseInt(e.target.value) || 20 })}
                min="1"
                max="300"
              />
              <span>초</span>
            </TimeInputGroup>
          </DesktopTimeSection>

          <DesktopTimeSection>
            <TimeLabel>문제 점수</TimeLabel>
            <TimeInputGroup>
              <DesktopTimeInput
                type="number"
                value={getCurrentQuestion().question_score || ''}
                onChange={(e) => updateCurrentQuestion({ question_score: e.target.value ? parseInt(e.target.value) : null })}
                min="0"
                max="10000"
                placeholder="0"
              />
              <span>점</span>
            </TimeInputGroup>
          </DesktopTimeSection>
        </div>

        {/* 문제 입력 섹션 */}
        <DesktopQuestionSection>
          
          <QuestionInputContainer>
            <QuestionInput
              placeholder="문제 내용을 입력하세요..."
              value={getCurrentQuestion().question}
              onChange={(e) => updateCurrentQuestion({ question: e.target.value })}
            />
          </QuestionInputContainer>
          
          {/* 첨부파일 표시 영역 */}
          {getCurrentQuestion().attachments.length > 0 && (
            <AttachmentSection>
              <AttachmentHeader 
                $isOpen={getCurrentQuestion().isAttachmentOpen}
                onClick={() => updateCurrentQuestion({ isAttachmentOpen: !getCurrentQuestion().isAttachmentOpen })}
              >
                <AttachmentTitle>
                  첨부파일 ({getCurrentQuestion().attachments.length}개)
                </AttachmentTitle>
                <AttachmentToggle $isOpen={getCurrentQuestion().isAttachmentOpen} />
              </AttachmentHeader>
              <AttachmentContent $isOpen={getCurrentQuestion().isAttachmentOpen}>
                <AttachmentPreview>
                  {getCurrentQuestion().attachments.map((attachment, index) => (
                    <AttachmentItem key={index}>
                      <DeleteAttachmentButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          removeAttachment(index);
                        }}
                        title="삭제"
                      >
                        ×
                      </DeleteAttachmentButton>
                      {attachment.type === 'image' ? (
                        <img src={attachment.url} alt={attachment.name} />
                      ) : attachment.type === 'audio' ? (
                        <audio controls src={attachment.url}>
                          Your browser does not support the audio element.
                        </audio>
                      ) : attachment.type === 'equation' ? (
                        <div style={{ 
                          fontSize: '18px', 
                          fontWeight: '500', 
                          color: '#2c3e50',
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          padding: '16px',
                          borderRadius: '12px',
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          {attachment.equation}
                        </div>
                      ) : null}
                      <AttachmentName>
                        {attachment.name}
                      </AttachmentName>
                    </AttachmentItem>
                  ))}
                </AttachmentPreview>
              </AttachmentContent>
            </AttachmentSection>
          )}
          
          <ChoicesContainer>
            {getCurrentQuestion().choices.map((choice, index) => {
              const currentQuestion = getCurrentQuestion()
              const correctCount = currentQuestion.choiceTypes.filter(type => type).length
              
              return (
                <ChoiceContainer key={index}>
                  <ChoiceWrapper
                    $isCorrect={currentQuestion.choiceTypes[index]}
                    $correctCount={correctCount}
                    $clickable={true}
                    onClick={() => handleChoiceTypeChange(index)}
                  >
                    <ChoiceInput
                      $isCorrect={currentQuestion.choiceTypes[index]}
                      placeholder={`선택지 ${index + 1}${index < 2 ? ' (필수)' : ' (선택)'}`}
                      value={choice}
                      onChange={(e) => handleChoiceChange(index, e.target.value)}
                      disabled={false}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <ChoiceTypeButton 
                      $isCorrect={currentQuestion.choiceTypes[index]}
                      $correctCount={correctCount}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleChoiceTypeChange(index)
                      }}
                    />
                  </ChoiceWrapper>
                </ChoiceContainer>
              )
            })}
          </ChoicesContainer>
        </DesktopQuestionSection>
      </LeftSection>

      <RightSection>
        {/* 현재 퀴즈 세트 안내 */}
        {quizObjects.length > 0 && (
          <div style={{ 
            padding: '15px 20px', 
            backgroundColor: '#e3f2fd', 
            borderRadius: '8px', 
            margin: '20px', 
            fontSize: '14px', 
            fontWeight: '600',
            color: '#1976d2',
            textAlign: 'center'
          }}>
            현재 퀴즈 세트: {getCurrentObjectDisplayText()}
          </div>
        )}
        
        {/* AR 스테이지 */}
        <DesktopARStageContainer>
          <DesktopStageSection>
            <DesktopARStage>
              {currentDisplayObject && (
                <ARObject>
                  <img 
                    src={currentDisplayObject?.image_url || currentDisplayObject?.image} 
                    alt={currentDisplayObject?.name} 
                  />
                </ARObject>
              )}
            </DesktopARStage>
            {(currentDisplayObject) && (
              <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600' }}>
                  {currentDisplayObject.name}
                </h3>
                {currentDisplayObject?.description && (
                  <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                    {currentDisplayObject.description}
                  </p>
                )}
              </div>
            )}
          </DesktopStageSection>
          
          {/* 하단 객체 전환 NavButton (TopBar에서 이동) */}
          {quizObjects.length > 1 && (
            <>
              <DesktopButtonSection>
                <NavButton
                  onClick={goToPrevObject}
                  disabled={currentObjectIndex === 0}
                  style={{ opacity: currentObjectIndex === 0 ? 0.5 : 1 }}
                >
                  <ChevronLeft size={20} />
                  이전 객체
                </NavButton>
                <span style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '8px 14px',
                  background: '#f5f3ff',
                  border: '1px solid #c4b5fd',
                  borderRadius: 10,
                  fontSize: 13, fontWeight: 700, color: '#5b21b6',
                }}>
                  {currentObjectIndex + 1} / {quizObjects.length} · {quizObjects[currentObjectIndex]?.name || '?'}
                </span>
                <NavButton
                  onClick={goToNextObject}
                  disabled={currentObjectIndex === quizObjects.length - 1}
                  style={{ opacity: currentObjectIndex === quizObjects.length - 1 ? 0.5 : 1 }}
                >
                  다음 객체
                  <ChevronRight size={20} />
                </NavButton>
              </DesktopButtonSection>
              {isEditMode && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 0 14px 0' }}>
                  <button
                    type="button"
                    onClick={removeObject}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 12px',
                      background: 'transparent',
                      color: '#dc2626',
                      border: '1px solid #fecaca',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={13} />
                    이 물체 삭제
                  </button>
                </div>
              )}
            </>
          )}
        </DesktopARStageContainer>
      </RightSection>

      {/* 모바일 레이아웃 (680px 이하) */}
      <MobileContainer>
        {/* 퀴즈 제목 표시 */}
        {title && (
          <QuizTitleHeader>
            <QuizTitleText>퀴즈 제목</QuizTitleText>
            <QuizTitle>{title}</QuizTitle>
            {questionId && <QuizId>ID: {questionId}</QuizId>}
          </QuizTitleHeader>
        )}
        
        {/* 현재 퀴즈 세트 안내 */}
        {quizObjects.length > 0 && (
          <div style={{ 
            padding: '10px 15px', 
            backgroundColor: '#e3f2fd', 
            borderRadius: '8px', 
            margin: '15px', 
            fontSize: '12px', 
            fontWeight: '600',
            color: '#1976d2',
            textAlign: 'center'
          }}>
            현재 퀴즈 세트: {getCurrentObjectDisplayText()}
          </div>
        )}
        
        {/* 작아진 AR 스테이지 */}
        <CompactARSection>
          <CompactARStage>
            <CompactARObject>
              <img 
                src={currentDisplayObject?.image_url || currentDisplayObject?.image} 
                alt={currentDisplayObject?.name} 
              />
            </CompactARObject>
          </CompactARStage>
          
          {/* 현재 선택된 객체 정보 */}
          {currentDisplayObject && (
            <div style={{ textAlign: 'center', padding: '10px', fontSize: '14px' }}>
              <h4 style={{ margin: '0 0 5px 0', fontWeight: '600' }}>
                {currentDisplayObject.name}
              </h4>
              {currentDisplayObject?.description && (
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                  {currentDisplayObject.description}
                </p>
              )}
            </div>
          )}
          <NavigationButtons>
            <MobileNavButton onClick={() => {
              if (isEditMode) {
                navigate(`/problem/${setId}`)
              } else {
                // 객체가 여러 개 추가된 상태에서 이전을 누르면 이전 객체로 이동
                if (quizObjects.length > 1 && currentObjectIndex > 0) {
                  // 현재 객체의 문제들을 저장하지 않고 이전 객체로 이동
                  console.log('🔙 이전 객체로 이동:', currentObjectIndex - 1);
                  setCurrentObjectIndex(currentObjectIndex - 1);
                } else {
                  // 첫 번째 객체이거나 객체가 1개인 경우 CreateDetail로 이동
                  navigate('/createdetail', {
                    state: {
                      title,
                      description,
                      isPrivate,
                      questionId,
                      selectedObjects: quizObjects,
                      currentObjectIndex: currentObjectIndex, // 현재 작업 중인 객체 인덱스 전달
                      isAddingMore: true,
                      activeTab,
                      selectedCategory,
                      coverImage,
                      excelDataByObject: excelDataByObjectState
                    }
                  })
                }
              }
            }}>&lt; {isEditMode ? '취소' : '이전'}</MobileNavButton>
            {quizObjects.length < 10 && (
              <MobileNavButton onClick={handleAddNextObject}>물체 추가 &gt;</MobileNavButton>
            )}
            {isEditMode && quizObjects.length > 1 && (
              <>
                <MobileNavButton
                  onClick={handlePreviousObject}
                  disabled={currentObjectIndex === 0}
                  style={{ opacity: currentObjectIndex === 0 ? 0.5 : 1 }}
                >
                  &lt; 이전 물체
                </MobileNavButton>
                <MobileNavButton
                  onClick={handleNextObjectInEdit}
                  disabled={currentObjectIndex === quizObjects.length - 1}
                  style={{ opacity: currentObjectIndex === quizObjects.length - 1 ? 0.5 : 1 }}
                >
                  다음 물체 &gt;
                </MobileNavButton>
                <MobileNavButton
                  onClick={removeObject}
                  style={{ backgroundColor: '#ff5252', color: 'white' }}
                >
                  물체 삭제
                </MobileNavButton>
              </>
            )}
          </NavigationButtons>
        </CompactARSection>
        
        {/* 문제 네비게이션 - 모바일 */}
        <div style={{ padding: '15px 20px 0' }}>
          <QuestionNavigationContainer>
            <QuestionTabs>
              {questions.map((q, index) => (
                <QuestionTab
                  key={q.id}
                  $isActive={index === currentQuestionIndex}
                  $hasContent={q.question.trim() !== ''}
                  onClick={() => setCurrentQuestionIndex(index)}
                >
                  {index + 1}
                </QuestionTab>
              ))}
              <AddQuestionTabButton onClick={addQuestion}>+</AddQuestionTabButton>
            </QuestionTabs>
            
            <QuestionInfo>
              <QuestionMeta>
                <span>문제 {currentQuestionIndex + 1} / {questions.length}</span>
                {isEditMode && quizObjects.length > 0 && (
                  <ObjectSelectWrapper>
                    <ObjectSelectLabel>객체</ObjectSelectLabel>
                    <ObjectSelect
                      value={getObjectKey(quizObjects[currentObjectIndex])}
                      onChange={(e) => handleObjectViewChange(e.target.value)}
                    >
                      {quizObjects.map((obj, idx) => {
                        const value = getObjectKey(obj)
                        return (
                          <option key={value || idx} value={value}>
                            {obj.name || `객체 ${idx + 1}`}
                          </option>
                        )
                      })}
                    </ObjectSelect>
                  </ObjectSelectWrapper>
                )}
              </QuestionMeta>
              <QuestionActions>
                <AddQuestionButton onClick={addQuestion}>문제 추가</AddQuestionButton>
                {questions.length > 1 && (
                  <DeleteQuestionButton 
                    onClick={() => removeQuestion(currentQuestionIndex)}
                    disabled={questions.length <= 1}
                  >
                    문제 삭제
                  </DeleteQuestionButton>
                )}
              </QuestionActions>
            </QuestionInfo>
          </QuestionNavigationContainer>
        </div>
        
        {/* 시간제한 바 */}
        <TimeBar>
          <TimeContainer>
            <span>시간제한</span>
            <TimeInput
              type="number"
              value={getCurrentQuestion().timeLimit}
              onChange={(e) => updateCurrentQuestion({ timeLimit: parseInt(e.target.value) || 20 })}
              min="1"
              max="300"
            />
            <span>초</span>
          </TimeContainer>
        </TimeBar>

        {/* 점수 설정 바 */}
        <TimeBar>
          <TimeContainer>
            <span>문제 점수</span>
            <TimeInput
              type="number"
              value={getCurrentQuestion().question_score || ''}
              onChange={(e) => updateCurrentQuestion({ question_score: e.target.value ? parseInt(e.target.value) : null })}
              min="0"
              max="10000"
              placeholder="점수"
            />
            <span>점</span>
          </TimeContainer>
        </TimeBar>

        {/* 문제 입력 섹션 */}
        <QuestionSection>
          
          <QuestionInputContainer>
            <QuestionInput
              placeholder="문제 내용을 입력하세요..."
              value={getCurrentQuestion().question}
              onChange={(e) => updateCurrentQuestion({ question: e.target.value })}
            />
          </QuestionInputContainer>
          
          {/* 첨부파일 표시 영역 */}
          {getCurrentQuestion().attachments.length > 0 && (
            <AttachmentSection>
              <AttachmentHeader 
                $isOpen={getCurrentQuestion().isAttachmentOpen}
                onClick={() => updateCurrentQuestion({ isAttachmentOpen: !getCurrentQuestion().isAttachmentOpen })}
              >
                <AttachmentTitle>
                  첨부파일 ({getCurrentQuestion().attachments.length}개)
                </AttachmentTitle>
                <AttachmentToggle $isOpen={getCurrentQuestion().isAttachmentOpen} />
              </AttachmentHeader>
              <AttachmentContent $isOpen={getCurrentQuestion().isAttachmentOpen}>
                <AttachmentPreview>
                  {getCurrentQuestion().attachments.map((attachment, index) => (
                    <AttachmentItem key={index}>
                      <DeleteAttachmentButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          removeAttachment(index);
                        }}
                        title="삭제"
                      >
                        ×
                      </DeleteAttachmentButton>
                      {attachment.type === 'image' ? (
                        <img src={attachment.url} alt={attachment.name} />
                      ) : attachment.type === 'audio' ? (
                        <audio controls src={attachment.url}>
                          Your browser does not support the audio element.
                        </audio>
                      ) : attachment.type === 'equation' ? (
                        <div style={{ 
                          fontSize: '18px', 
                          fontWeight: '500', 
                          color: '#2c3e50',
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          padding: '16px',
                          borderRadius: '12px',
                          border: '1px solid #e2e8f0',
                          textAlign: 'center'
                        }}>
                          {attachment.equation}
                        </div>
                      ) : null}
                      <AttachmentName>
                        {attachment.name}
                      </AttachmentName>
                    </AttachmentItem>
                  ))}
                </AttachmentPreview>
              </AttachmentContent>
            </AttachmentSection>
          )}
          
          <ChoicesContainer>
            {getCurrentQuestion().choices.map((choice, index) => {
              const currentQuestion = getCurrentQuestion()
              const correctCount = currentQuestion.choiceTypes.filter(type => type).length
              
              return (
                <ChoiceContainer key={index}>
                  <ChoiceWrapper
                    $isCorrect={currentQuestion.choiceTypes[index]}
                    $correctCount={correctCount}
                    $clickable={true}
                    onClick={() => handleChoiceTypeChange(index)}
                  >
                    <ChoiceInput
                      $isCorrect={currentQuestion.choiceTypes[index]}
                      placeholder={`선택지 ${index + 1}${index < 2 ? ' (필수)' : ' (선택)'}`}
                      value={choice}
                      onChange={(e) => handleChoiceChange(index, e.target.value)}
                      disabled={false}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <ChoiceTypeButton 
                      $isCorrect={currentQuestion.choiceTypes[index]}
                      $correctCount={correctCount}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleChoiceTypeChange(index)
                      }}
                    />
                  </ChoiceWrapper>
                </ChoiceContainer>
              )
            })}
          </ChoicesContainer>
        </QuestionSection>
        
      </MobileContainer>
      
      {/* 수식 입력 모달 */}
      {showMathModal && <MathModal />}
    </Container>
    {/* AR 요소 설정 패널 제거 — 처음엔 2D로 등록하므로 위치/크기/회전/애니메이션 불필요 */}
    </>
  )
}
